/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Dropdown, Modal, Popover, Spin, Table } from "antd";
import { useSnackbar } from "notistack";
import { Ban, CheckCircle, CreditCard, HelpCircle, Mail, ShieldCheck, XCircle, FileText, Gauge, Clock, Layers } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { useContextState } from "../../../context/ContextProvider";
import { addToNestedHeader } from "../../../features/nested_header/nested_header";
import { useIsLargeScreen } from "../../../hooks/useIsLargeScreen.jsx";
import AddAccountModal from "../../../modals/AddAccountModal";
import AddRestoreOrgModal from "../../../modals/AddRestoreOrgModal";
import AdminManualSubscriptionModal from "../../../modals/AdminManualSubscriptionModal";
import AdminBulkApiModal from "../../../modals/AdminBulkApiModal";
import ExtendRetentionModal from "../../../modals/ExtendRetentionModal";
import ManageAddonsModal from "../../../modals/ManageAddonsModal";
import ConfirmModal from "../../../modals/ConfirmModal";
import DeleteOrgModal from "../../../modals/DeleteOrgModal";
import DisconnectOrgModal from "../../../modals/DisconnectOrgModal";
import RevokeConnectionModal from "../../../modals/RevokeConnectionModal";
import SendXeroReauthEmailModal from "../../../modals/SendXeroReauthEmailModal";
import SendXeroSubscriptionEmailModal from "../../../modals/SendXeroSubscriptionEmailModal";
import SuccessModal from "../../../modals/SuccessModal";
import QbdXeroPaymentResultModal from "../../../modals/QbdXeroPaymentResultModal";
import AddOrgTypeModal from "../../../modals/AddOrgTypeModal";
import QuickBooksConnectButton from "../../../components/QuickBooksConnectButton";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AddCardModal from "../../../modals/AddCardModal";
import { setAddCardModalOpen } from "../../../store/common";
import AddButton from "../../../shared/AddButton";
import Support from "../../../shared/Support";
import BillingWelcomeNotice, {
  isBillingNoticeDismissed,
} from "../../../components/BillingWelcomeNotice";
import MultiSubscribeWizardModal from "../../../components/MultiSubscribeWizardModal";
import {
  buildBulkSubscribePayload,
  buildInitialOrgPlans,
  getDefaultOrgPlan,
  getOrgKey,
  hasPlanAddonsChanged,
  isOrgEligibleForSubscribe,
  isPayrollSubscribeEligible,
} from "../../../utils/subscribeModules";
import { getDefaultMethod, getPaymentMethods } from "../../../utils/payment_methods";
import { Tooltip } from "antd";
import { getRestoreDemoVideo } from "../../../utils/users";

import {
  setAccountCounts,
  setDemoTourFinished,
} from "../../../store/common.js";
import { SERVER_URL } from "../../../utils/axios.js";
import { formatDate } from "../../../utils/luxonDateTimeHelper.js";
import {
  addOrg,
  addQboOrg,
  bulkSubscribeXeroWithPlans,
  bulkSubscribeQboWithPlans,
  cancelSubscription,
  cancelQboSubscription,
  getOrganizations,
  getQboOrganizations,
  updateSubscriptionAddons,
  updateBackupRetention,
  // QBO-only actions (currently disabled; keep for later)
  // qboReassign,
  // qboSync,
  subUpdate,
  subQboUpdate,
  xeroClearData,
  qboClearData,
  initiateQBDToXeroPayment,
  disconnectXero,
  disconnectQbo,
  revokeXeroConnection,
  revokeQboConnection,
} from "../../../utils/organization";
import { organisationPage } from "../constants";
import "./accounts.scss";

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`);

const Accounts = ({ onOrgIdChange, onAttachmentSelected, type = "all" }) => {
  const combinedMode = type === "all";
  const providerLabel = (p) => (p === "qbo" ? "QuickBooks" : "Xero");
  const providerIconPath = (p) =>
    p === "qbo"
      ? "/assets/intuit/qb-logo-icon.svg"
      : "/assets/icons/xero.svg";
  const orgProvider = (org) =>
    org.provider ?? (type === "qbo" ? "qbo" : "xero");

  const normalizeAccounts = (accounts, provider) =>
    (accounts || []).map((row) => ({
      ...row,
      provider,
      statusKey: `${provider}-${row.id}`,
    }));

  const mapProgressByStatusKey = (obj, provider) => {
    const out = {};
    Object.keys(obj || {}).forEach((id) => {
      out[`${provider}-${id}`] = obj[id];
    });
    return out;
  };

  const { isLoading, getIdTokenClaims, getAccessTokenSilently, user: auth0User } =
    useAuth0();
  const billingNoticeUserKey = auth0User?.sub || auth0User?.email || "";
  const { enqueueSnackbar } = useSnackbar();
  const isLargeScreen = useIsLargeScreen();
  const [videoUrl, setVideoUrl] = useState(null);

  const { setBreadCrumb, role } = useContextState();
  const { setNestedHeader } = useContextState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedOrgAction, setSelectedOrgAction] = useState(false);

  const [addAccModalOpen, setAddAccModalOpen] = useState(false);
  const [removeOrgModalOpen, setRemoveOrgModalOpen] = useState(false);
  const [revokeConnectionModalOpen, setRevokeConnectionModalOpen] =
    useState(false);
  const [vaultDowngradeAccountName, setVaultDowngradeAccountName] =
    useState("");
  const [revokeConnectionAccountName, setRevokeConnectionAccountName] =
    useState("");
  const [unsubOrgModalOpen, setUnsubOrgModalOpen] = useState(false);
  const [restoreOrgSelected, setRestoreOrgSelected] = useState("");
  const [restoreOrgIdSelected, setRestoreOrgIdSelected] = useState();
  const [restoreOrgBackupDates, setRestoreOrgBackupDates] = useState([]);
  const [restoreSourceCountryCode, setRestoreSourceCountryCode] = useState("");
  const [restorePreFill, setRestorePreFill] = useState({
    initialBackupDate: null,
    initialTargetOrgId: null,
    initialTargetOrgName: null,
    skipVideo: false
  });
  const [deleteOrgModalOpen, setDeleteOrgModalOpen] = useState(false);
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
    useState(false);
  const [selectedOrgDetails, setSelectedOrgDetails] = useState(null);

  const [deleteOrgSelected, setDeleteOrgSelected] = useState();
  const [deleteTargets, setDeleteTargets] = useState(null);
  const [selectedOrgActionProvider, setSelectedOrgActionProvider] =
    useState("xero");
  const [restoreModalProvider, setRestoreModalProvider] = useState("xero");

  const [sendReauthModalOpen, setSendReauthModalOpen] = useState(false);
  const [reauthOrgId, setReauthOrgId] = useState(null);
  const [reauthOrgName, setReauthOrgName] = useState("");
  const [reauthOrgEmail, setReauthOrgEmail] = useState("");
  const [reauthOrgProvider, setReauthOrgProvider] = useState("xero");

  const [sendSubscriptionModalOpen, setSendSubscriptionModalOpen] = useState(false);
  const [subscriptionOrgId, setSubscriptionOrgId] = useState(null);
  const [subscriptionOrgName, setSubscriptionOrgName] = useState("");
  const [subscriptionOrgEmail, setSubscriptionOrgEmail] = useState("");
  const [subscriptionOrgProvider, setSubscriptionOrgProvider] = useState("xero");

  const [connectQboModalOpen, setConnectQboModalOpen] = useState(false);
  const [connectQboModalAccountName, setConnectQboModalAccountName] = useState("");

  const [adminManualModalOpen, setAdminManualModalOpen] = useState(false);
  const [adminManualOrg, setAdminManualOrg] = useState(null);
  const [adminManualOrgs, setAdminManualOrgs] = useState([]);

  const [adminBulkApiModalOpen, setAdminBulkApiModalOpen] = useState(false);
  const [adminBulkApiOrg, setAdminBulkApiOrg] = useState(null);

  const [retentionModalOpen, setRetentionModalOpen] = useState(false);
  const [retentionOrg, setRetentionOrg] = useState(null);

  const [manageAddonsModalOpen, setManageAddonsModalOpen] = useState(false);
  const [manageAddonsOrg, setManageAddonsOrg] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [backupStatus, setBackupStatus] = useState({});
  const [restoreStatus, setRestoreStatus] = useState({});

  const [tableDataLoading, setTableDataLoading] = useState(false);

  const [cancelSubLoading, setCancelSubLoading] = useState(false);
  const [addOrgLoading, setAddOrgLoading] = useState(false);
  const [isCancelSubDisabled, setIsCancelSubDisabled] = useState(false);
  const [addOrgTypeModalOpen, setAddOrgTypeModalOpen] = useState(false);

  const [openedIndex, setOpenedIndex] = useState(0);
  const [successTitle, setSuccessTitle] = useState("");
  const [successText, setSuccessText] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalCloseHandler, setSuccessModalCloseHandler] = useState(
    () => () => {
      setSuccessModalOpen(false);
    }
  );

  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [confirmOnOkay, setConfirmOnOkay] = useState(() => () => {});
  const dispatch = useDispatch();

  // Inside your component
  const location = useLocation();
  const navigate = useNavigate();
  const isDisconnectedQbo =
    new URLSearchParams(location.search).get("disconnected") === "qbo";
  const [showDisconnectedBanner, setShowDisconnectedBanner] =
    useState(isDisconnectedQbo);

  useEffect(() => {
    if (isDisconnectedQbo) {
      setShowDisconnectedBanner(true);
    }
  }, [isDisconnectedQbo]);

  const alertShownRef = useRef(false); // To prevent double alert
  const supportRef = useRef(null); // Support component ref for triggering from dropdown

  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is open

  // QBO-only actions state (currently disabled; keep for later)
  // const [qboReassignModalOpen, setQboReassignModalOpen] = useState(false);
  // const [qboReassignOrg, setQboReassignOrg] = useState(null); // { id, account }
  // const [qboReassignUserId, setQboReassignUserId] = useState("");
  // const [qboReassignLoading, setQboReassignLoading] = useState(false);

  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [backupFilter, setBackupFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return (
      sp.get("searchQuery") ||
      sp.get("search") ||
      sp.get("owner") ||
      sp.get("user") ||
      ""
    );
  });
  const [searchType, setSearchType] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("owner") || sp.get("user")) return "owner";
    return sp.get("searchType") || "org";
  });
  const [billingEmptyNoticeDismissed, setBillingEmptyNoticeDismissed] =
    useState(false);

  useEffect(() => {
    setBillingEmptyNoticeDismissed(
      isBillingNoticeDismissed(billingNoticeUserKey)
    );
  }, [billingNoticeUserKey]);

  useEffect(() => {
    const syncBillingDismissed = () => {
      setBillingEmptyNoticeDismissed(
        isBillingNoticeDismissed(billingNoticeUserKey)
      );
    };
    window.addEventListener("wowzer:billing-notice-dismissed", syncBillingDismissed);
    window.addEventListener("storage", syncBillingDismissed);
    return () => {
      window.removeEventListener(
        "wowzer:billing-notice-dismissed",
        syncBillingDismissed
      );
      window.removeEventListener("storage", syncBillingDismissed);
    };
  }, [billingNoticeUserKey]);

  useEffect(() => {
    setSubscriptionFilter("");
    setPlatformFilter("");
  }, [type]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.has("reset")) {
      setSearchType("org");
      setSearchQuery("");
      return;
    }
    const ownerParam = sp.get("owner") || sp.get("user");
    const searchTypeParam = sp.get("searchType");
    const searchQueryParam = sp.get("searchQuery") || sp.get("search");

    if (ownerParam) {
      setSearchType("owner");
      setSearchQuery(ownerParam);
    } else if (searchQueryParam !== null) {
      if (searchTypeParam) setSearchType(searchTypeParam);
      setSearchQuery(searchQueryParam);
    } else if (!location.search) {
      setSearchType("org");
      setSearchQuery("");
    }
  }, [location.search]);

  useEffect(() => {
    if (!combinedMode) {
      setSelectedOrgActionProvider(type === "qbo" ? "qbo" : "xero");
    }
  }, [type, combinedMode]);

  const [multiSubscribeOpen, setMultiSubscribeOpen] = useState(false);
  const [multiSubscribeOrgs, setMultiSubscribeOrgs] = useState([]);
  const [multiSubscribePlans, setMultiSubscribePlans] = useState({});
  const [multiSubscribeStepIndex, setMultiSubscribeStepIndex] = useState(0);
  const [multiSubscribeShowSummary, setMultiSubscribeShowSummary] = useState(false);
  const [multiSubscribeConfirming, setMultiSubscribeConfirming] = useState(false);
  const [hasDefaultPaymentMethod, setHasDefaultPaymentMethod] = useState(false);
  const [paymentCards, setPaymentCards] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [disconnectCouponCode, setDisconnectCouponCode] = useState("");
  const [disconnectCouponDiscount, setDisconnectCouponDiscount] = useState(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  const [qbdToXeroModalOpen, setQbdToXeroModalOpen] = useState(false);
  const [numberOfMigrations, setNumberOfMigrations] = useState(1);
  const [xeroOrgSetupOption, setXeroOrgSetupOption] = useState('new'); // 'new' or 'existing'
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Payment result modal state
  const [paymentResultModalOpen, setPaymentResultModalOpen] = useState(false);
  const [paymentResultData, setPaymentResultData] = useState({
    type: null, // 'success', 'cancelled', or 'error'
    numberOfMigrations: null,
    status: null,
    message: null
  });

  // QBD to Xero pricing constants
  const QBD_XERO_PRICING = {
    NEW_ORG: 100,
    EXISTING_ORG: 350
  };

  // Get current price based on selected option
  const getCurrentPrice = () => {
    return xeroOrgSetupOption === 'new' ? QBD_XERO_PRICING.NEW_ORG : QBD_XERO_PRICING.EXISTING_ORG;
  };

  useEffect(() => {
    if (!isLoading) {
      fetchOrgs();
      setSelectedRowKeys([]);
    }

    if (!isLoading && !alertShownRef.current) {
      const params = new URLSearchParams(location.search);
      const alertMessage = params.get("alert");

      if (alertMessage) {
        alertShownRef.current = true; // mark as shown
        const isDuplicateOrgAlert =
          /already in your account/i.test(alertMessage) ||
          /already named/i.test(alertMessage);
        enqueueSnackbar(alertMessage, {
          variant: isDuplicateOrgAlert ? "error" : "info",
          autoHideDuration: isDuplicateOrgAlert ? 10000 : 5000,
        });

        // Remove `alert` from the URL
        params.delete("alert");
        const newSearch = params.toString();
        const newUrl = `${window.location.pathname}${
          newSearch ? `?${newSearch}` : ""
        }`;
        window.history.replaceState({}, "", newUrl);
      }

      // Check for QBD to Xero payment result parameters
      const qbdPaymentStatus = params.get("qbd_payment");

      if (qbdPaymentStatus) {
        // Extract all payment-related parameters
        const paymentData = {
          type: qbdPaymentStatus, // 'success', 'cancelled', or 'error'
          numberOfMigrations: params.get("numberOfMigrations") ? parseInt(params.get("numberOfMigrations")) : null,
          status: params.get("status"),
          message: params.get("message") ? decodeURIComponent(params.get("message")) : null
        };

        // Set payment result data and open modal
        setPaymentResultData(paymentData);
        setPaymentResultModalOpen(true);

        // Clean up URL by removing payment-related query parameters
        params.delete("qbd_payment");
        params.delete("numberOfMigrations");
        params.delete("status");
        params.delete("message");

        const newSearch = params.toString();
        const newUrl = `${window.location.pathname}${
          newSearch ? `?${newSearch}` : ""
        }`;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [isLoading, location.search, type]);

  const fetchOrgs = async () => {
    try {
      const token = await getIdTokenClaims();

      setTableDataLoading(true);

      const finishPendingRestoreIfNeeded = (newOrgs) => {
        const pendingRestore = sessionStorage.getItem("pendingRestoreFlow");
        if (!pendingRestore) return;
        try {
          const state = JSON.parse(pendingRestore);
          const newOrg = newOrgs.find(
            (org) => !state.existingOrgIds.includes(org.id)
          );
          if (newOrg) {
            const sourceOrg = newOrgs.find(
              (org) => String(org.id) === String(state.sourceOrgId)
            );
            setRestoreOrgSelected(state.sourceOrgName);
            setRestoreOrgIdSelected(state.sourceOrgId);
            setRestoreOrgBackupDates(state.backupDates);
            setRestoreSourceCountryCode(sourceOrg?.country_code || "");
            setRestoreModalProvider(newOrg.provider || "xero");
            setRestorePreFill({
              initialBackupDate: state.backupDate,
              initialTargetOrgId: newOrg.id,
              initialTargetOrgName: newOrg.account,
              skipVideo: true,
            });
            sessionStorage.removeItem("pendingRestoreFlow");
          } else {
            sessionStorage.removeItem("pendingRestoreFlow");
          }
        } catch (error) {
          console.error("Error processing pending restore flow:", error);
          sessionStorage.removeItem("pendingRestoreFlow");
        }
      };

      const checkPendingAddOrgSubscribeIfNeeded = (newOrgs) => {
        const pendingAdd = sessionStorage.getItem("pendingAddOrgSubscribe");
        if (!pendingAdd) return;
        sessionStorage.removeItem("pendingAddOrgSubscribe");
        try {
          let targetOrgId = null;
          if (pendingAdd !== "true") {
            const state = JSON.parse(pendingAdd);
            if (Array.isArray(state?.existingOrgIds)) {
              const addedOrg = newOrgs.find(
                (org) => !state.existingOrgIds.includes(org.id)
              );
              if (addedOrg) {
                targetOrgId = addedOrg.id;
              }
            }
          }
          openMultiSubscribeWizard(targetOrgId, newOrgs);
        } catch (e) {
          console.error("Error checking pending add org subscribe:", e);
          openMultiSubscribeWizard(null, newOrgs);
        }
      };

      const handleSingleProviderResponse = (res, provider) => {
        const newOrgs = normalizeAccounts(res?.data?.accounts, provider);
        setOrganizations(newOrgs);
        dispatch(setAccountCounts(newOrgs.length));
        const progress =
          res?.data?.progress ??
          res?.data?.backupProgress ??
          res?.data?.backup_progress ??
          {};
        const restoreProgress =
          res?.data?.restoreProgress ??
          res?.data?.restore_progress ??
          res?.data?.restoreStatus ??
          {};
        setBackupStatus(mapProgressByStatusKey(progress, provider));
        setRestoreStatus(mapProgressByStatusKey(restoreProgress, provider));
        res.data.demoTourFinished == "0"
          ? dispatch(setDemoTourFinished(false))
          : dispatch(setDemoTourFinished(true));
        finishPendingRestoreIfNeeded(newOrgs);
        checkPendingAddOrgSubscribeIfNeeded(newOrgs);
      };

      if (combinedMode) {
        Promise.all([
          getOrganizations(token.__raw),
          getQboOrganizations(token.__raw),
        ])
          .then(([xr, qr]) => {
            const xeroOrgs = normalizeAccounts(xr?.data?.accounts, "xero");
            const qboOrgs = normalizeAccounts(qr?.data?.accounts, "qbo");
            const merged = [...xeroOrgs, ...qboOrgs];
            setOrganizations(merged);
            dispatch(setAccountCounts(merged.length));

            const xProgress =
              xr?.data?.progress ??
              xr?.data?.backupProgress ??
              xr?.data?.backup_progress ??
              {};
            const qProgress =
              qr?.data?.progress ??
              qr?.data?.backupProgress ??
              qr?.data?.backup_progress ??
              {};
            const xRestore =
              xr?.data?.restoreProgress ??
              xr?.data?.restore_progress ??
              xr?.data?.restoreStatus ??
              {};
            const qRestore =
              qr?.data?.restoreProgress ??
              qr?.data?.restore_progress ??
              qr?.data?.restoreStatus ??
              {};
            setBackupStatus({
              ...mapProgressByStatusKey(xProgress, "xero"),
              ...mapProgressByStatusKey(qProgress, "qbo"),
            });
            setRestoreStatus({
              ...mapProgressByStatusKey(xRestore, "xero"),
              ...mapProgressByStatusKey(qRestore, "qbo"),
            });
            xr?.data?.demoTourFinished == "0" ||
            qr?.data?.demoTourFinished == "0"
              ? dispatch(setDemoTourFinished(false))
              : dispatch(setDemoTourFinished(true));
            finishPendingRestoreIfNeeded(merged);
            checkPendingAddOrgSubscribeIfNeeded(merged);
          })
          .catch((err) => {
            console.log("err", err);
          })
          .finally(() => {
            setTableDataLoading(false);
          });
      } else {
        const fetchApi =
          type === "qbo" ? getQboOrganizations : getOrganizations;
        const provider = type === "qbo" ? "qbo" : "xero";
        fetchApi(token.__raw)
          .then((res) => {
            handleSingleProviderResponse(res, provider);
          })
          .catch((err) => {
            console.log("err", err);
          })
          .finally(() => {
            setTableDataLoading(false);
          });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVideoUrl = async () => {
    try {

      const token = await getIdTokenClaims();
      const response = await getRestoreDemoVideo(token.__raw); // adjust if needed
      if (response.data.success) {
        setVideoUrl(response.data.url);
      } else {
        console.error("Failed to load video URL");
      }
    } catch (error) {
      console.error("Video fetch error", error);
    } finally {
      
    }
  };

  const onRestoreModalClose = () => {
    setRestoreOrgSelected("");
    setRestoreOrgIdSelected("");
    setRestoreOrgBackupDates([]);
    setRestoreSourceCountryCode("");
    setRestorePreFill({
      initialBackupDate: null,
      initialTargetOrgId: null,
      initialTargetOrgName: null,
      skipVideo: false
    });
  };

  const handleAddNewOrg = () => {
    setAddOrgTypeModalOpen(true);
  };

  const handleProviderSelect = (providerKey) => {
    setAddOrgTypeModalOpen(false);
    (async () => {
      try {
        setAddOrgLoading(true); // Show full-page loader
        let token = await getIdTokenClaims();

        sessionStorage.setItem(
          "pendingAddOrgSubscribe",
          JSON.stringify({
            timestamp: Date.now(),
            existingOrgIds: organizations.map((o) => o.id),
          })
        );

        // Small delay to ensure loader is visible
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (providerKey === "qbo") {
          addQboOrg(token.__raw);
        } else {
          addOrg(token.__raw);
        }
      } catch (e) {
        console.error(e);
        sessionStorage.removeItem("pendingAddOrgSubscribe");
        setAddOrgLoading(false); // Hide loader on error
      }
    })();
  };

  const handleQBDToXero = () => {
    setQbdToXeroModalOpen(true);
    setPaymentError(null);
  };

  const incrementMigrations = () => {
    setNumberOfMigrations(prev => prev + 1);
  };

  const decrementMigrations = () => {
    setNumberOfMigrations(prev => Math.max(1, prev - 1));
  };

  const handleQBDPayment = async () => {
    try {
      setPaymentLoading(true);
      setPaymentError(null);

      const token = await getIdTokenClaims();
      const response = await initiateQBDToXeroPayment(
        token.__raw,
        numberOfMigrations,
        xeroOrgSetupOption
      );

      if (response.data && response.data.stripe_url) {
        // Open Stripe payment URL in new tab
        window.open(response.data.stripe_url, '_blank');
        // Close the modal after successful payment initiation
        setQbdToXeroModalOpen(false);
        // Reset states
        setNumberOfMigrations(1);
        setXeroOrgSetupOption('new');
      } else {
        setPaymentError('Payment URL not received from server');
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      setPaymentError(
        error.response?.data?.message ||
        'Failed to initiate payment. Please try again.'
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const removeDataFromTable = () => {
    setOrganizations([]);
  };

  const handleOrgDeleted = () => {
    setDeleteTargets(null);
    setSelectedRowKeys([]);
    removeDataFromTable();
    fetchOrgs();
    setDeleteOrgModalOpen(false);
  };

  const handleSubscribeUpdate = async () => {
    try {
      const token = await getIdTokenClaims();

      const subUpdateApi =
        selectedOrgActionProvider === "qbo" ? subQboUpdate : subUpdate;

      subUpdateApi(token.__raw, selectedOrgAction)
        .then((res) => {
          if (res.data.success) {
            setSuccessTitle("Vault plan activated.");
            setSuccessText(
              "You have successfully activated valut plan for the organization!"
            );
            setSuccessModalOpen(true);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDisconnected = async (couponCode = null) => {
    try {
      setDisconnectLoading(true);
      const token = await getIdTokenClaims();
      setTableDataLoading(true);
      const disconnectApi =
        selectedOrgActionProvider === "qbo" ? disconnectQbo : disconnectXero;
      const codeToUse = couponCode || disconnectCouponCode;
      const res = await disconnectApi(token.__raw, selectedOrgAction, codeToUse);
      const ok = res?.data?.success === true || res?.data?.success === "true";
      const isQboVault = selectedOrgActionProvider === "qbo";
      if (ok) {
        enqueueSnackbar(
          res?.data?.message ||
            res?.data?.msg ||
            (isQboVault
              ? "Vault downgrade started. Complete payment if you are redirected."
              : "Organization switched to Vault."),
          { variant: "success" }
        );
        setRemoveOrgModalOpen(false);
        fetchOrgs();

        // QBO commonly returns redirect_url (often back to /organizations or to checkout).
        const redirectUrl = res?.data?.redirect_url;
        if (redirectUrl) {
          try {
            const u = new URL(redirectUrl, window.location.origin);
            if (u.origin === window.location.origin) {
              navigate(`${u.pathname}${u.search}`, { replace: true });
            } else {
              window.location.assign(redirectUrl);
            }
          } catch {
            window.location.assign(redirectUrl);
          }
        } else {
          navigate("/organizations", { replace: true });
        }
      } else {
        enqueueSnackbar(
          res?.data?.message || res?.data?.msg || "Vault downgrade was not successful.",
          { variant: "warning" }
        );
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar(
        e.response?.data?.message ||
          e.response?.data?.msg ||
          (selectedOrgActionProvider === "qbo"
            ? "Failed to downgrade QuickBooks organization to Vault."
            : "Failed to switch organization to Vault."),
        { variant: "error" }
      );
    } finally {
      setDisconnectLoading(false);
      setTableDataLoading(false);
    }
  };

  const handleRevokeConnection = async () => {
    try {
      const token = await getIdTokenClaims();
      setTableDataLoading(true);
      const revokeApi =
        selectedOrgActionProvider === "qbo"
          ? revokeQboConnection
          : revokeXeroConnection;
      const res = await revokeApi(token.__raw, selectedOrgAction);
      const ok = res?.data?.success === true || res?.data?.success === "true";
      if (ok) {
        enqueueSnackbar(
          res?.data?.message ||
            res?.data?.msg ||
            (selectedOrgActionProvider === "qbo"
              ? "QuickBooks connection disconnected."
              : "Accounting connection removed."),
          { variant: "success" }
        );
        setRevokeConnectionModalOpen(false);
        fetchOrgs();
      } else {
        enqueueSnackbar(
          res?.data?.message || res?.data?.msg || "Disconnect was not successful.",
          { variant: "warning" }
        );
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar(
        e.response?.data?.message ||
          e.response?.data?.msg ||
          "Failed to disconnect organization.",
        { variant: "error" }
      );
    } finally {
      setTableDataLoading(false);
    }
  };

  const handleClearExistingBackup = (id, provider) => {
    setConfirmModalOpen(true);
    setConfirmTitle("Clear existing backup");
    setConfirmText(
      "Do you want to clear existing backup data for the organization?"
    );
    setConfirmOnOkay(() => () => {
      clearExistingBackup(id, provider);
      setConfirmModalOpen(false);
    });
  };

  const clearExistingBackup = async (id, provider) => {
    try {
      const token = await getIdTokenClaims();

      setTableDataLoading(true);

      const clearDataApi =
        provider === "qbo" ? qboClearData : xeroClearData;

      clearDataApi(token.__raw, id)
        .then((res) => {
          if (res.data.success) {
            setSuccessModalOpen(true);
            setSuccessTitle("Cleared existing backup");
            setSuccessText(
              "Successfully cleared the backup data for the organization"
            );

            fetchOrgs();
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setTableDataLoading(false);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const handleRestoreSubmit = () => {
    setSuccessTitle("Restore Queueed");
    setSuccessText("Your restore request has been submitted successfully.");

    setSuccessModalOpen(true);

    setSuccessModalCloseHandler(() => () => {
      fetchOrgs();
    });
  };

  const popupContent = (statusKey, kind = "backup") => {
    let progress = null;
    if (kind == "backup") {
      if (backupStatus) {
        progress = backupStatus[statusKey];
      }
    } else {
      if (restoreStatus) {
        progress = restoreStatus[statusKey];
      }
    }

    if (progress == null || (progress && progress.moduleStatus == null)) {
      return <div>N/A</div>;
    }
    const moduleStatus = progress.moduleStatus;

    // QBO returns moduleStatus with PascalCase keys (e.g. Account, Attachable).
    // The legacy UI below expects Xero-style snake_case keys, so render QBO generically.
    const moduleKeys = moduleStatus ? Object.keys(moduleStatus) : [];
    const isQboModuleStatus =
      moduleKeys.length > 0 &&
      moduleKeys.some((k) => /^[A-Z]/.test(k)) &&
      !("accounts" in moduleStatus);

    if (isQboModuleStatus) {
      return (
        <div className="max-h-80 overflow-y-auto pr-1">
          {moduleKeys
            .slice()
            .sort((a, b) => a.localeCompare(b))
            .map((k) => (
              <div
                key={k}
                className="flex justify-between border-b border-gray-200 p-1 items-center text-gray-600 status-icons gap-2"
              >
                <div>{k}</div>
                <div>{printStatusIcon(moduleStatus[k])}</div>
              </div>
            ))}
        </div>
      );
    }

    return (
      <div className={"lg:flex w-full gap-5"}>
        <div className={"lg:border-r border-gray-200 lg:pr-5"}>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.accounts ? "" : " hidden")
            }
          >
            <div>Accounts</div>
            <div>{printStatusIcon(moduleStatus.accounts)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.balance_sheet ? "" : " hidden")
            }
          >
            <div>Balance Sheet</div>
            <div>{printStatusIcon(moduleStatus.balance_sheet)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.batch_payments ? "" : " hidden")
            }
          >
            <div>Batch Payments</div>
            <div>{printStatusIcon(moduleStatus.batch_payments)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.branding_themes ? "" : " hidden")
            }
          >
            <div>Branding Themes</div>
            <div>{printStatusIcon(moduleStatus.branding_themes)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.contacts ? "" : " hidden")
            }
          >
            <div>Contacts</div>
            <div>{printStatusIcon(moduleStatus.contacts)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.credit_notes ? "" : " hidden")
            }
          >
            <div>Credit Notes</div>
            <div>{printStatusIcon(moduleStatus.credit_notes)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.currencies ? "" : " hidden")
            }
          >
            <div>Currencies</div>
            <div>{printStatusIcon(moduleStatus.currencies)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.invoices ? "" : " hidden")
            }
          >
            <div>Invoices</div>
            <div>{printStatusIcon(moduleStatus.invoices)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.items ? "" : " hidden")
            }
          >
            <div>Items</div>
            <div>{printStatusIcon(moduleStatus.items)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.journals ? "" : " hidden")
            }
          >
            <div>Journals</div>
            <div>{printStatusIcon(moduleStatus.journals)}</div>
          </div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.linked_transactions ? "" : " hidden")
            }
          >
            <div>Linked Transactions</div>
            <div>{printStatusIcon(moduleStatus.linked_transactions)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.manual_journals ? "" : " hidden")
            }
          >
            <div>Manual Journals</div>
            <div>{printStatusIcon(moduleStatus.manual_journals)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.organisations ? "" : " hidden")
            }
          >
            <div>Organisations</div>
            <div>{printStatusIcon(moduleStatus.organisations)}</div>
          </div>
        </div>

        <div>
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.payments ? "" : " hidden")
            }
          >
            <div>Payments</div>
            <div>{printStatusIcon(moduleStatus.payments)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.pre_payments ? "" : " hidden")
            }
          >
            <div>Pre-payments</div>
            <div>{printStatusIcon(moduleStatus.pre_payments)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.profit_and_loss ? "" : " hidden")
            }
          >
            <div>Profit & Loss</div>
            <div>{printStatusIcon(moduleStatus.profit_and_loss)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.purchase_orders ? "" : " hidden")
            }
          >
            <div>Purchase Orders</div>
            <div>{printStatusIcon(moduleStatus.purchase_orders)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.quotes ? "" : " hidden")
            }
          >
            <div>Quotes</div>
            <div>{printStatusIcon(moduleStatus.quotes)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.repeating_invoices ? "" : " hidden")
            }
          >
            <div>Repeating Invoices</div>
            <div>{printStatusIcon(moduleStatus.repeating_invoices)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.tax_rates ? "" : " hidden")
            }
          >
            <div>Tax Rates</div>
            <div>{printStatusIcon(moduleStatus.tax_rates)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.tracking_categories ? "" : " hidden")
            }
          >
            <div>Tracking Categories</div>
            <div>{printStatusIcon(moduleStatus.tracking_categories)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.transactions ? "" : " hidden")
            }
          >
            <div>Transactions</div>
            <div>{printStatusIcon(moduleStatus.transactions)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.transfers ? "" : " hidden")
            }
          >
            <div>Transfers</div>
            <div>{printStatusIcon(moduleStatus.transfers)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.trial_balance ? "" : " hidden")
            }
          >
            <div>Trial Balance</div>
            <div>{printStatusIcon(moduleStatus.trial_balance)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.users ? "" : " hidden")
            }
          >
            <div>Users</div>
            <div>{printStatusIcon(moduleStatus.users)}</div>
          </div>

          {/* Payroll Modules */}
          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.payitems ? "" : " hidden")
            }
          >
            <div>Pay Items</div>
            <div>{printStatusIcon(moduleStatus.payitems)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.PayrollCalendars ? "" : " hidden")
            }
          >
            <div>Payroll Calendars</div>
            <div>{printStatusIcon(moduleStatus.PayrollCalendars)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.SuperFunds ? "" : " hidden")
            }
          >
            <div>Super Funds</div>
            <div>{printStatusIcon(moduleStatus.SuperFunds)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.employees_detailed ? "" : " hidden")
            }
          >
            <div>Employees Detailed</div>
            <div>{printStatusIcon(moduleStatus.employees_detailed)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.LeaveApplications ? "" : " hidden")
            }
          >
            <div>Leave Applications</div>
            <div>{printStatusIcon(moduleStatus.LeaveApplications)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.Timesheets ? "" : " hidden")
            }
          >
            <div>Timesheets</div>
            <div>{printStatusIcon(moduleStatus.Timesheets)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.detailed_payruns ? "" : " hidden")
            }
          >
            <div>Detailed Pay Runs</div>
            <div>{printStatusIcon(moduleStatus.detailed_payruns)}</div>
          </div>

          <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.payslips ? "" : " hidden")
            }
          >
            <div>Payslips</div>
            <div>{printStatusIcon(moduleStatus.payslips)}</div>
          </div>

          {/* <div
            className={
              "flex justify-between border-b border-gray-300 p-1 items-center text-gray-600 status-icons  gap-2" +
              (moduleStatus.users ? "" : " hidden")
            }
          >
            <div>Files & Folders</div>
            <div>{printStatusIcon(moduleStatus.files_folder)}</div>
          </div> */}
        </div>
      </div>
    );
  };

  const printStatusIcon = (status) => {
    if (!status) return;
    if (status.toLowerCase() == "completed") {
      return (
        <ReactSVG
          src={"/assets/icons/green-check.svg"}
          className={"text-green-500"}
        />
      );
    } else if (status.toLowerCase() == "pending") {
      return (
        <ReactSVG
          src={"/assets/icons/pending-icon.svg"}
          className={"text-green-500"}
        />
      );
    } else if (status.toLowerCase() == "running" || status.toLowerCase() == "attachments-processing") {
      return (
        <ReactSVG
          src={"/assets/icons/loader.svg"}
          className={"text-green-500"}
        />
      );
    }
  };

  const printRestoreStatus = (status, pausedReason) => {
    if (status == "pending") {
      return "Pending";
    } else if (status == "running") {
      return "In progress";
    } else if (status == "completed") {
      return "Completed";
    } else if (status == "seeking-assistance") {
      return "Seeking Assistance";
    } else if (status == "paused") {
      if (pausedReason) return "Paused (" + pausedReason + ")";
      return "Paused";
    } else if (status == "unpaid") {
      return "Unpaid";
    } else {
      return "N/A";
    }
  };
  const printBackupStatus = (status, pausedReason) => {
    if (status == "pending") {
      return "Pending";
    } else if (status == "running") {
      return "In progress";
    } else if (status == "completed") {
      return "Completed";
    } else if (status == "file-folder-pending") {
      return "Files pending";
    } else if (status == "files-processing") {
      return "Files processing";
    } else if (status == "history-notes-pending") {
      return "History pending";
    } else if (status == "history-notes-processing") {
      return "History processing";
    } else if (status == "attachments-processing") {
      return "Attachments";
    } else if (
      status == "paused" ||
      status == "paused_with_file_folder" ||
      status == "paused_with_history_notes"
    ) {
      let suffix = "";
      if (status == "paused_with_file_folder") suffix = " (Files)";
      else if (status == "paused_with_history_notes") suffix = " (History)";
      
      if (pausedReason) return "Paused (" + pausedReason + ")" + suffix;
      return "Paused" + suffix;
    } else {
      return status || "N/A";
    }
  };

  const formatStatusTime = (value) => {
    if (!value) return "";
    const v = String(value).trim();
    // Backend sometimes already prefixes labels like "Started:" / "Completed:"
    return v.replace(/^(started|completed)\s*:\s*/i, "").trim();
  };

  const isOrgInTrial = (org) =>
    org?.is_trial_org == 1 || org?.is_trial_org === true;

  const OrgTrialBadge = () => (
    <span className="ml-auto flex-shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
      Trial
    </span>
  );

  const getSubscriptionStatus = (org) => {
    const {
      is_vault_activated,
      is_trial_org,
      need_re_authentication,
      is_trial_expired,
      org_status,
      admin_billing_mode,
    } = org;
    const pname = providerLabel(orgProvider(org));
    const manualSuffix = admin_billing_mode == 1 ? " (Manual)" : "";
    if (is_vault_activated == 1) {
      return `Vault${manualSuffix}`;
    } else if (is_trial_org == 1) {
      return "Trial";
    } else if (org_status == 1) {
      return need_re_authentication == 1
        ? `Connect with ${pname}`
        : `Subscribed${manualSuffix}`;
    } else if (is_trial_expired == 1) {
      return "Trial Expired";
    } else if (org_status == 0) {
      return need_re_authentication == 1
        ? (pname === "QuickBooks" ? "Connect with QuickBooks" : `Reconnect with ${pname}`)
        : `Not Subscribed${manualSuffix}`;
    } else {
      return `Not Subscribed${manualSuffix}`;
    }
  };

  const columns = [
    {
      title: "Organization",
      dataIndex: "account",
      key: "account",
      width: role === "superadmin" ? "25%" : "30%",
      ellipsis: true,
      sorter: (a, b) =>
        String(a.account || "").localeCompare(String(b.account || ""), undefined, {
          sensitivity: "base",
        }),
      render: (_, record) => {
        const { id, account, estimated_days_to_complete_backup } = record;
        const prov = orgProvider(record);
        if (account)
        return (
          <div
            onClick={() => {
              const nextPage = {
                header: account,
                page: organisationPage.ORGANISATION_FOLDER,
              };
              dispatch(addToNestedHeader(nextPage));
              setNestedHeader(nextPage);
              setBreadCrumb([]);
              setBreadCrumb((prevState) => [
                ...prevState,
                { label: account, url: window.location.href },
              ]);
              onOrgIdChange(id, prov);
            }}
            className={"cursor-pointer flex items-center gap-2 min-w-0 w-full"}
          >
            <ReactSVG
              src={providerIconPath(prov)}
              className="w-5 h-5 flex-shrink-0 provider-icon"
            />
            <span className="truncate flex-1 min-w-0">{account}</span>
            {isOrgInTrial(record) ? <OrgTrialBadge /> : null}
            {prov === "xero" && estimated_days_to_complete_backup > 0 && (
           <Tooltip
            overlayStyle={{ minWidth: 400 }} // Sets the tooltip box width
            onOpenChange={(visible) => {
              if (visible && !videoUrl) {
                fetchVideoUrl();
              }
            }}
           title={
             <div onClick={(e) => e.stopPropagation()}>
               <div>
                 <strong>Important Note on Your First Xero Backup</strong> <br/><br/>
               </div>
               <div style={{ whiteSpace: "normal" }}>
                 Xero allows up to <strong>5000 API calls per organization per day.</strong> Since this is your
                 <strong> first backup</strong> and your organization includes 
                 <strong> attachments,</strong> we need to back up all data—making the process more time-consuming.
                 Based on this, your initial backup may take up to
                 <strong> {estimated_days_to_complete_backup} </strong> day(s) to complete.
                 <br /> <br/>
                 To speed things up, we offer an <strong>optional shortcut:</strong> you can manually download your attachments
                 from Xero and upload them to our platform.
                 This helps us save API calls and significantly reduces the time needed for your first backup.
                 <br /><br/>
                 Don't worry—this is a <strong>one-time step.</strong> Future backups will be incremental and much faster.
                 <br /> <br/>
                 We've created a simple tutorial to guide you through downloading and uploading your attachments. <br/>
                 <a
                   href={videoUrl}
                   target="_blank"
                   rel="noopener noreferrer"
                   onClick={(e) => e.stopPropagation()}
                   style={{ color: "#1890ff", fontWeight: "500", textDecoration: "underline" }}
                 >
                   👉 Click here to watch the video.
                 </a>
               </div>
             </div>
           }
         >
           <span
             style={{
               marginLeft: 8,
               color: "#1890ff",
               fontSize: 16,
               cursor: "pointer",
               display: "inline-block",
             }}
             onClick={(e) => e.stopPropagation()}
           >
             <strong> ? </strong>
           </span>
         </Tooltip>
         
         
           
            )}
          </div>
        );
        
      },
    },
    role === "superadmin" ? {
      title: "Owner",
      dataIndex: "org_owner_email",
      key: "org_owner_email",
      width: "15%",
      ellipsis: true,
      sorter: (a, b) =>
        String(a.org_owner_email || "").localeCompare(
          String(b.org_owner_email || ""),
          undefined,
          { sensitivity: "base" }
        ),
      render: (_, { org_owner_email }) => (
        <div className="text-gray-500">
          {org_owner_email || "N/A"}
        </div>
      ),
    } : null,
    {
      title: "Subscription",
      dataIndex: "subscription",
      key: "subscription",
	  width: role === "superadmin" ? "15%" : "20%",
      ellipsis: true,
      sorter: (a, b) =>
        getSubscriptionStatus(a).localeCompare(getSubscriptionStatus(b), undefined, {
          sensitivity: "base",
        }),
      render: (_, orgInfo) => {
        if (orgInfo.subscription)
          return (
            <div className="text-gray-500 flex gap-2 items-center">
              {(orgInfo.subscription == 1 || orgInfo.subscription == 0) &&
              orgInfo.need_re_authentication == 1 ? (
                <HelpCircle className="text-yellow-500 w-5 h-5" />
              ) : orgInfo.subscription == 1 ||
                orgInfo.is_vault_activated == 1 ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : orgInfo.is_trial_expired == 1 ? (
                <XCircle className="text-red-500 w-5 h-5" />
              ) : null}

              {getSubscriptionStatus(orgInfo)}
            </div>
          );
      },
    },
    {
      title: "Backup Status",
      dataIndex: "status",
      key: "backup_status",
	  width: role === "superadmin" ? "15%" : "20%",
      ellipsis: true,
      sorter: (a, b) => {
        const label = (org) =>
          printBackupStatus(
            backupStatus?.[org.statusKey]?.backupStatus ?? "",
            backupStatus?.[org.statusKey]?.pausedReason ?? ""
          );
        return label(a).localeCompare(label(b), undefined, { sensitivity: "base" });
      },
      render: (_, { id, status, statusKey, subscription }) => {
        const bStatus = backupStatus?.[statusKey]?.backupStatus;
        const isCompleted = [
          "completed",
          "attachments-processing",
          "file-folder-pending",
          "files-processing",
          "history-notes-pending",
          "history-notes-processing",
        ].includes(bStatus);
        const isPaused = [
          "paused",
          "paused_with_file_folder",
          "paused_with_history_notes",
        ].includes(bStatus);

        return (
          <div className={"flex justify-content-center gap-4"}>
            <div className={"text-gray-500 flex gap-2 items-center"}>
              {status.map((item, index) => (
                <Popover
                  key={item.id || `${id}-${index}`}
                  content={popupContent(statusKey)}
                  title={
                    <div className="flex justify-between w-full">
                      <span>Status</span>
                      <span className="text-xs text-gray-500">
                        {formatStatusTime(backupStatus[statusKey]?.completeTime)}
                      </span>
                    </div>
                  }
                >
                  <div className={"flex items-center"}>
                    {index > 0 && <div className={"mx-4 text-gray-300"}>|</div>}
                    <div
                      className={`h-2 w-2 rounded-full mr-2 ${
                        item?.failed || isPaused
                          ? "bg-red-300"
                          : bStatus
                          ? isCompleted
                            ? "bg-green-300"
                            : "bg-orange-300"
                          : item?.progress
                          ? "bg-orange-300"
                          : "bg-green-300"
                      }`}
                    />
                    {/* {item?.message} */}
                    {printBackupStatus(
                      backupStatus?.[statusKey]?.backupStatus ?? "",
                      backupStatus?.[statusKey]?.pausedReason ?? ""
                    )}
                  </div>
                </Popover>
              ))}
            </div>
          </div>
        );
      },
    },
    {
      title: "Restore Status",
      dataIndex: "status",
      key: "restore_status",
	  width: "15%",
      ellipsis: true,
      sorter: (a, b) => {
        const label = (org) => {
          const rs = restoreStatus[org.statusKey];
          return rs
            ? printRestoreStatus(rs.restoreStatus, rs.pausedReason)
            : "";
        };
        return label(a).localeCompare(label(b), undefined, { sensitivity: "base" });
      },
      render: (_, { statusKey }) => {
        return (
          <div className={"flex justify-content-center gap-4"}>
            <div
              className={
                "text-gray-500 flex gap-2 items-center " +
                (restoreStatus[statusKey] ? "" : "hidden")
              }
            >
              <Popover
                content={popupContent(statusKey, "restore")}
                title={
                  <div className="flex justify-between w-full">
                    <span>Status</span>
                    <span className="text-xs text-gray-500">
                      {restoreStatus[statusKey]?.completeTime}
                    </span>
                  </div>
                }
              >
                <div className={"flex items-center"}>
                  <div
                    className={`h-2 w-2 rounded-full mr-2 ${
                      restoreStatus[statusKey]
                        ? restoreStatus[statusKey].restoreStatus == "completed"
                          ? "bg-green-300"
                          : "bg-orange-300"
                        : ""
                    }`}
                  />
                  {restoreStatus[statusKey] &&
                    printRestoreStatus(
                      restoreStatus[statusKey].restoreStatus,
                      restoreStatus[statusKey].pausedReason
                    )}
                </div>
              </Popover>
            </div>
          </div>
        );
      },
    },
    {
      title: "Renewal Date",
      dataIndex: "subscription_renewal_date",
      key: "subscription_renewal_date",
      width: "10%",
      ellipsis: true,
      sorter: (a, b) =>
        String(a.subscription_renewal_date || "\uffff").localeCompare(
          String(b.subscription_renewal_date || "\uffff")
        ),
      render: (_, { subscription_renewal_date }) => (
        <div className={"relative"}>
          {formatDate(subscription_renewal_date)}
        </div>
      ),
    },

    role === "superadmin" ? {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: "10%",
      ellipsis: true,
      sorter: (a, b) =>
        String(a.created_at || "\uffff").localeCompare(String(b.created_at || "\uffff")),
      render: (_, { created_at }) => (
        <div className="text-gray-500">
          {created_at ? formatDate(created_at) : "N/A"}
        </div>
      ),
    } : null,

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
	  width: "5%",
      ellipsis: true,
      render: (
        _,
        rowData
      ) => {
        const {
          id,
          account,
          org_owner_email,
          org_status,
          backup_dates,
          need_re_authentication,
          restrict_options_except_delete,
          disable_vault_and_restore_option,
          is_vault_activated,
          is_suscription_cancel_requested,
          is_trial_expired,
          statusKey,
          provider,
          country_code,
        } = rowData;
        const rowProv = provider ?? (type === "qbo" ? "qbo" : "xero");
        return (
          <Dropdown
            placement="bottomRight"
            trigger={["click"]}
            open={openDropdownId === statusKey}
            onOpenChange={(open) => setOpenDropdownId(open ? statusKey : null)}
            dropdownRender={(menu) => (
              <div className="bg-white w-60 p-2 space-y-1 border-gray-100 rounded-lg shadow-lg">
                {role === "superadmin" && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm text-blue-700 hover:text-blue-800"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setAdminManualOrg({
                        id,
                        account,
                        provider: rowProv,
                        org_status,
                        subscription_status: rowData?.subscription_status,
                        subscription_type: rowData?.subscription_type,
                        subscription_renewal_date: rowData?.subscription_renewal_date,
                        admin_billing_mode: rowData?.admin_billing_mode,
                        files: rowData?.files ?? 0,
                        history_and_notes: rowData?.history_and_notes ?? 0,
                        payroll: rowData?.payroll ?? 0,
                      });
                      setAdminManualModalOpen(true);
                    }}
                  >
                    <ShieldCheck className="w-4 h-4 mr-2 text-blue-600" />
                    Manual / Offline Billing
                  </Button>
                )}

                {role === "superadmin" && (rowProv === "xero" || rowProv === "qbo") && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm text-indigo-700 hover:text-indigo-800"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setAdminBulkApiOrg({
                        id,
                        account,
                        provider: rowProv,
                      });
                      setAdminBulkApiModalOpen(true);
                    }}
                  >
                    <Gauge className="w-4 h-4 mr-2 text-indigo-600" />
                    Bulk API Settings
                  </Button>
                )}

                {role === "superadmin" && (rowProv === "xero" || rowProv === "qbo") && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setSubscriptionOrgId(id);
                      setSubscriptionOrgName(account);
                      setSubscriptionOrgEmail(org_owner_email || "");
                      setSubscriptionOrgProvider(rowProv);
                      setSendSubscriptionModalOpen(true);
                    }}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Send subscription email
                  </Button>
                )}
                {role === "superadmin" && (rowProv === "xero" || rowProv === "qbo") && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setReauthOrgId(id);
                      setReauthOrgName(account);
                      setReauthOrgEmail(org_owner_email || "");
                      setReauthOrgProvider(rowProv);
                      setSendReauthModalOpen(true);
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send reauth email
                  </Button>
                )}

                {need_re_authentication == 1 && (
                  <Button
                    className={`w-full border-none flex justify-start shadow-none font-medium text-sm`}
                    onClick={() => {
                      setOpenDropdownId(null);
                      if (rowProv === "qbo") {
                        setConnectQboModalAccountName(account);
                        setConnectQboModalOpen(true);
                      } else {
                        handleProviderSelect(rowProv);
                      }
                    }}
                    disabled={restrict_options_except_delete == 1}
                    aria-label={`Connect ${account} with ${providerLabel(
                      rowProv
                    )}`}
                  >
                    <HelpCircle className="text-yellow-500 w-5 h-5" />
                    Connect with {providerLabel(rowProv)}
                  </Button>
                )}

                {/*
                QBO-only actions (currently disabled; keep for later)
                {rowProv === "qbo" && org_status == 1 && need_re_authentication != 1 && (
                  <>
                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null);
                        handleQboSync(id);
                      }}
                      disabled={restrict_options_except_delete == 1}
                    >
                      <ReactSVG src={"/assets/icons/arrow-clockwise.svg"} />
                      Sync
                    </Button>

                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null);
                        setQboReassignOrg({ id, account });
                        setQboReassignUserId("");
                        setQboReassignModalOpen(true);
                      }}
                      disabled={restrict_options_except_delete == 1}
                    >
                      <ReactSVG src={"/assets/icons/user.svg"} />
                      Reassign
                    </Button>
                  </>
                )}
                */}

                {(org_status == 1 || is_vault_activated == 1 || is_trial_expired == 1  ) && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setRestoreOrgSelected(account);
                      setRestoreOrgIdSelected(id);
                      setRestoreOrgBackupDates(backup_dates);
                      setRestoreSourceCountryCode(country_code || "");
                      setRestoreModalProvider(rowProv);
                    }}
                    disabled={
                      restrict_options_except_delete == 1 ||
                      disable_vault_and_restore_option == 1
                    }
                  >
                    <ReactSVG src={"/assets/icons/arrow-clockwise.svg"} />
                    Restore
                  </Button>
                )}

                {org_status == 1 ? (
                  <>
                    {is_suscription_cancel_requested == 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm text-blue-600 hover:text-blue-700"
                        onClick={() => {
                          setOpenDropdownId(null);
                          openMultiSubscribeWizard(statusKey || id);
                        }}
                      >
                        <ReactSVG
                          src={providerIconPath(rowProv)}
                          className="provider-icon"
                        />
                        Subscribe / Change Plan
                      </Button>
                    )}
                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        openDowngradeModal(id, account, rowProv);
                      }}
                      disabled={
                        restrict_options_except_delete == 1 ||
                        disable_vault_and_restore_option == 1
                      }
                    >
                      <ReactSVG src={"/assets/icons/folder.svg"} />
                      Downgrade to Vault
                    </Button>
                    {rowProv === "qbo" && need_re_authentication != 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setRevokeConnectionAccountName(account);
                          setRevokeConnectionModalOpen(true);
                          setSelectedOrgAction(id);
                          setSelectedOrgActionProvider(rowProv);
                        }}
                      >
                        <ReactSVG src={"/assets/icons/cancel.svg"} />
                        Disconnect
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {is_vault_activated != 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          openDowngradeModal(id, account, rowProv);
                        }}
                        disabled={
                          restrict_options_except_delete == 1 ||
                          disable_vault_and_restore_option == 1
                        }
                      >
                        <ReactSVG src={"/assets/icons/folder.svg"} />
                        Downgrade to Vault
                      </Button>
                    )}
                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null);
                        openMultiSubscribeWizard(statusKey || id);
                      }}
                      disabled={
                        (restoreStatus[statusKey] != null &&
                          restoreStatus[statusKey]?.restoreStatus !=
                            "completed") ||
                        restrict_options_except_delete == 1
                      }
                    >
                      <ReactSVG
                        src={providerIconPath(rowProv)}
                        className="provider-icon"
                      />
                      {is_vault_activated == 1
                        ? "Upgrade to Subscribe"
                        : "Subscribe"}
                    </Button>
                    {rowProv === "qbo" && need_re_authentication != 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setRevokeConnectionAccountName(account);
                          setRevokeConnectionModalOpen(true);
                          setSelectedOrgAction(id);
                          setSelectedOrgActionProvider(rowProv);
                        }}
                      >
                        <ReactSVG src={"/assets/icons/cancel.svg"} />
                        Disconnect
                      </Button>
                    )}
                  </>
                )}

                {org_status == 1 && is_vault_activated != 1 && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm text-gray-700 hover:text-blue-700"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setRetentionOrg({
                        ...rowData,
                        id,
                        account,
                        name: account,
                        provider: rowProv,
                        country_code,
                      });
                      setRetentionModalOpen(true);
                    }}
                    disabled={restrict_options_except_delete == 1}
                  >
                    <Clock className="w-4 h-4 mr-2 text-blue-600" />
                    Extended Retention
                  </Button>
                )}

                {org_status == 1 && is_vault_activated != 1 && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm text-gray-700 hover:text-indigo-700"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setManageAddonsOrg({
                        ...rowData,
                        id,
                        account,
                        name: account,
                        provider: rowProv,
                        country_code,
                      });
                      setManageAddonsModalOpen(true);
                    }}
                    disabled={restrict_options_except_delete == 1}
                  >
                    <Layers className="w-4 h-4 mr-2 text-indigo-600" />
                    Manage Add-ons
                  </Button>
                )}

                <Button
                  className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                  onClick={() => {
                    setOpenDropdownId(null);
                    handleClearExistingBackup(id, rowProv);
                  }}
                  disabled={restrict_options_except_delete == 1}
                >
                  <ReactSVG src={"/assets/icons/broom.svg"} />
                  Clear Existing Backup Data
                </Button>
                <Button
                  className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                  onClick={() => {
                    setOpenDropdownId(null);
                    const nextPage = {
                      header: account,
                      page: organisationPage.ORGANISATION_FOLDER,
                    };
                    dispatch(addToNestedHeader(nextPage));
                    setNestedHeader(nextPage);
                    setBreadCrumb([]);
                    setBreadCrumb((prevState) => [
                      ...prevState,
                      { label: account, url: window.location.href },
                    ]);

                    onOrgIdChange(id, rowProv);
                  }}
                  disabled={restrict_options_except_delete == 1}
                >
                  <ReactSVG src={"/assets/icons/folder.svg"} />
                  Show Backups
                </Button>

                <Button
                  className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                  onClick={() => {
                    setOpenDropdownId(null);
                    setDeleteOrgModalOpen(true);
                    setDeleteOrgSelected(account);
                    setDeleteTargets([
                      { id: Number(id), provider: rowProv },
                    ]);
                  }}
                  danger
                >
                  <ReactSVG src={"/assets/icons/delete.svg"} />
                  Delete
                </Button>
                {org_status == 1 && is_vault_activated != 1 && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setCancelSubscriptionModalOpen(true);
                      setSelectedOrgDetails({ id, provider: rowProv, isVault: false });
                    }}
                    danger
                    disabled={isCancelSubDisabled || is_suscription_cancel_requested == 1} // 🔒 disable on success
                  >
                    {cancelSubLoading ? (
                      <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    ) : (
                      <Ban className="w-4 h-4" />
                    )}
                    Cancel Subscription
                  </Button>
                )}

                {is_vault_activated == 1 && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                    onClick={() => {
                      setOpenDropdownId(null);
                      setCancelSubscriptionModalOpen(true);
                      setSelectedOrgDetails({ id, provider: rowProv, isVault: true });
                    }}
                    danger
                    disabled={isCancelSubDisabled || is_suscription_cancel_requested == 1}
                  >
                    {cancelSubLoading ? (
                      <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    ) : (
                      <Ban className="w-4 h-4" />
                    )}
                    Cancel Vault Subscription
                  </Button>
                )}

                {restoreStatus[statusKey]?.restoreStatus === "completed" && restoreStatus[statusKey]?.restoreQueueId && (
                  <Button
                    className="w-full border-none flex justify-start shadow-none font-medium text-sm text-indigo-700 hover:text-indigo-800"
                    onClick={() => {
                      setOpenDropdownId(null);
                      navigate(`/restore-report/${rowProv}/${restoreStatus[statusKey].restoreQueueId}`);
                    }}
                  >
                    <FileText className="w-4 h-4" />
                    View Restore Report
                  </Button>
                )}

                <Button
                  className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                  onClick={() => {
                    setOpenDropdownId(null); // Close the dropdown
                    if (supportRef.current) {
                      supportRef.current.openWithOrganization(id, account);
                    }
                  }}
                >
                  <ReactSVG src={"/assets/icons/support.svg"} />
                  Get Support
                </Button>

                <Button
                  className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                  onClick={() => {
                    setOpenDropdownId(null);
                    const nextPage = {
                      header: account,
                      page: organisationPage.ORGANISATION_ATTACHMENTS,
                    };
                    dispatch(addToNestedHeader(nextPage));
                    setNestedHeader(nextPage);
                    setBreadCrumb([]);
                    setBreadCrumb((prevState) => [
                      ...prevState,
                      { label: account, url: window.location.href },
                    ]);
                    onAttachmentSelected(id, rowProv);
                  }}
                  disabled={restrict_options_except_delete == 1}
                >
                  <ReactSVG src={"/assets/icons/folder.svg"} />
                  Attachments
                </Button>
				{/*<Button
                  className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                  onClick={() => {
                    setOpenDropdownId(null); // Close the dropdown
                    if (supportRef.current) {
                      supportRef.current.openWithOrganization(id, account);
                    }
                  }}
                >
                  <ReactSVG src={"/assets/icons/support.svg"} />
                  Get Support
                </Button>*/}
              </div>
            )}
          >
            <button
              type="button"
              className="p-0 border-0 bg-transparent cursor-pointer rounded-lg hover:bg-white inline-flex items-center justify-center"
              aria-label={`Open actions for ${account}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ReactSVG
                className="p-1 org-three-dots rounded-lg"
                src={"/assets/icons/dot-menu.svg"}
              />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    columnWidth: 48,
  };

  const filteredOrganizations = organizations.filter((org) => {
    let matchesSearch = true;
    if (searchQuery) {
      const q = searchQuery.trim().toLowerCase();
      if (searchType === "owner") {
        // Search by owner email
        matchesSearch = (
          org.org_owner_email?.toLowerCase().includes(q) ||
          org.email?.toLowerCase().includes(q)
        );
      } else {
        // Search by organization name (default)
        matchesSearch = org.account
          ?.toLowerCase()
          .includes(q);
      }
    }

    const subStatus = getSubscriptionStatus(org);
    const matchesSubscription =
      !subscriptionFilter || subStatus === subscriptionFilter;

    let orgBackupStatus = printBackupStatus(
      backupStatus?.[org.statusKey]?.backupStatus ?? "",
      backupStatus?.[org.statusKey]?.pausedReason ?? ""
    );

    if (orgBackupStatus.toLowerCase().includes("paused")) {
      orgBackupStatus = "Paused";
    } // for paused only

    const matchesBackup = !backupFilter || orgBackupStatus === backupFilter;

    const matchesPlatform =
      !platformFilter || org.provider === platformFilter;

    return (
      matchesSearch &&
      matchesSubscription &&
      matchesBackup &&
      matchesPlatform
    );
  });

  const mobileRender = filteredOrganizations.map((item, index) => {
    const {
      id,
      account,
      org_owner_email,
      org_status,
      backup_dates,
      need_re_authentication,
      restrict_options_except_delete,
      disable_vault_and_restore_option,
      is_vault_activated,
      is_suscription_cancel_requested,
      is_trial_expired,
      statusKey,
      provider,
      country_code,
    } = item;
    const rowM = provider ?? (type === "qbo" ? "qbo" : "xero");
    return (
      <div
        key={statusKey}
        className="border rounded-lg border-gray-300 text-sm py-3 mt-3 px-4 text-gray-500"
        onClick={() => {
          setOpenedIndex(index);
        }}
      >
        <div className={"flex items-center justify-between cursor-pointer"}>
          <div className={"flex items-center gap-2"}>
            <ReactSVG
              src="/assets/icons/common-dropdown.svg"
              className={`text-gray-700 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full ${
                openedIndex == index && "rotate-180"
              }`}
            />
            <div className={"text-gray-500 font-medium flex items-center gap-2 min-w-0 w-full"}>
              <ReactSVG
                src={providerIconPath(rowM)}
                className="w-5 h-5 flex-shrink-0 provider-icon"
              />
              <span className="truncate flex-1 min-w-0">{item.account}</span>
              {isOrgInTrial(item) ? <OrgTrialBadge /> : null}
              {rowM === "xero" && item.estimated_days_to_complete_backup > 0 && (
                <Tooltip
                overlayStyle={{ minWidth: 400 }} // Sets the tooltip box width
                onOpenChange={(visible) => {
                  if (visible && !videoUrl) {
                    fetchVideoUrl();
                  }
                }}
               title={
                 <div onClick={(e) => e.stopPropagation()}>
                   <div>
                     <strong>Important Note on Your First Xero Backup</strong> <br/><br/>
                   </div>
                   <div style={{ whiteSpace: "normal" }}>
                     Xero allows up to <strong>5000 API calls per organization per day.</strong> Since this is your
                     <strong> first backup</strong> and your organization includes 
                     <strong> attachments,</strong> we need to back up all data—making the process more time-consuming.
                     Based on this, your initial backup may take up to
                     <strong> {item.estimated_days_to_complete_backup} </strong> day(s) to complete.
                     <br /> <br/>
                     To speed things up, we offer an <strong>optional shortcut:</strong> you can manually download your attachments
                     from Xero and upload them to our platform.
                     This helps us save API calls and significantly reduces the time needed for your first backup.
                     <br /><br/>
                     Don't worry—this is a <strong>one-time step.</strong> Future backups will be incremental and much faster.
                     <br /> <br/>
                     We've created a simple tutorial to guide you through downloading and uploading your attachments. <br/>
                     <a
                       href={videoUrl}
                       target="_blank"
                       rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       style={{ color: "#1890ff", fontWeight: "500", textDecoration: "underline" }}
                     >
                       👉 Click here to watch the video.
                     </a>
                   </div>
                 </div>
               }
             >
               <span
                 style={{
                   marginLeft: 8,
                   color: "#1890ff",
                   fontSize: 16,
                   cursor: "pointer",
                   display: "inline-block",
                 }}
                 onClick={(e) => e.stopPropagation()}
               >
                 <strong> ? </strong>
               </span>
             </Tooltip>
              )}
               

            </div>
          </div>
          <div className={"flex justify-end items-center gap-2"}>
            {item.status.length ? (
              <div className={"text-gray-500 flex gap-2 items-center"}>
                {item.status.map((item, index) => (
                  <Popover
                    key={item.id || index}
                    content={popupContent(statusKey)}
                    title="Status"
                  >
                    <div className={"flex items-center"}>
                      {index > 0 && (
                        <div className={"mx-4 text-gray-300"}>|</div>
                      )}
                      <div
                        className={`h-2 w-2 rounded-full mr-2 ${
                          item?.failed
                            ? "bg-red-300"
                            : item?.progress
                            ? "bg-orange-300"
                            : "bg-green-300"
                        }`}
                      />
                      {item?.message}
                    </div>
                  </Popover>
                ))}
              </div>
            ) : (
              ""
            )}
            <div className="right-0 z-10 flex justify-between items-center -top-[4px]">
              <Dropdown
                placement="bottomRight"
                trigger={["click"]}
                open={openDropdownId === statusKey}
                onOpenChange={(open) =>
                  setOpenDropdownId(open ? statusKey : null)
                }
                dropdownRender={(menu) => (
                  <div className="bg-white w-60 p-2 space-y-1 border-gray-100 rounded-lg shadow-lg">
                    {role === "superadmin" && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm text-blue-700 hover:text-blue-800"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setAdminManualOrg({
                            id,
                            account,
                            provider: rowM,
                            org_status,
                            subscription_status: item?.subscription_status,
                            subscription_type: item?.subscription_type,
                            subscription_renewal_date: item?.subscription_renewal_date,
                            admin_billing_mode: item?.admin_billing_mode,
                            files: item?.files ?? 0,
                            history_and_notes: item?.history_and_notes ?? 0,
                            payroll: item?.payroll ?? 0,
                          });
                          setAdminManualModalOpen(true);
                        }}
                      >
                        <ShieldCheck className="w-4 h-4 mr-2 text-blue-600" />
                        Manual / Offline Billing
                      </Button>
                    )}

                    {role === "superadmin" && (rowM === "xero" || rowM === "qbo") && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm text-indigo-700 hover:text-indigo-800"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setAdminBulkApiOrg({
                            id,
                            account,
                            provider: rowM,
                          });
                          setAdminBulkApiModalOpen(true);
                        }}
                      >
                        <Gauge className="w-4 h-4 mr-2 text-indigo-600" />
                        Bulk API Settings
                      </Button>
                    )}

                    {role === "superadmin" && (rowM === "xero" || rowM === "qbo") && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setSubscriptionOrgId(id);
                          setSubscriptionOrgName(account);
                          setSubscriptionOrgEmail(org_owner_email || "");
                          setSubscriptionOrgProvider(rowM);
                          setSendSubscriptionModalOpen(true);
                        }}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Send subscription email
                      </Button>
                    )}
                    {role === "superadmin" && (rowM === "xero" || rowM === "qbo") && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setReauthOrgId(id);
                          setReauthOrgName(account);
                          setReauthOrgEmail(org_owner_email || "");
                          setReauthOrgProvider(rowM);
                          setSendReauthModalOpen(true);
                        }}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send reauth email
                      </Button>
                    )}

                    {need_re_authentication == 1 && (
                      <Button
                        className={`w-full border-none flex justify-start shadow-none font-medium text-sm`}
                        onClick={() => {
                          setOpenDropdownId(null);
                          if (rowM === "qbo") {
                            setConnectQboModalAccountName(account);
                            setConnectQboModalOpen(true);
                          } else {
                            handleProviderSelect(rowM);
                          }
                        }}
                        disabled={restrict_options_except_delete == 1}
                        aria-label={`Connect ${account} with ${providerLabel(
                          rowM
                        )}`}
                      >
                        <HelpCircle className="text-yellow-500 w-5 h-5" />
                        Connect with {providerLabel(rowM)}
                      </Button>
                    )}

                    {/*
                    QBO-only actions (currently disabled; keep for later)
                    {rowM === "qbo" && org_status == 1 && need_re_authentication != 1 && (
                      <>
                        <Button
                          className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                          onClick={() => {
                            setOpenDropdownId(null);
                            handleQboSync(id);
                          }}
                          disabled={restrict_options_except_delete == 1}
                        >
                          <ReactSVG src={"/assets/icons/arrow-clockwise.svg"} />
                          Sync
                        </Button>

                        <Button
                          className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                          onClick={() => {
                            setOpenDropdownId(null);
                            setQboReassignOrg({ id, account });
                            setQboReassignUserId("");
                            setQboReassignModalOpen(true);
                          }}
                          disabled={restrict_options_except_delete == 1}
                        >
                          <ReactSVG src={"/assets/icons/user.svg"} />
                          Reassign
                        </Button>
                      </>
                    )}
                    */}

                    {(org_status == 1 || is_vault_activated == 1 || is_trial_expired == 1) && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setRestoreOrgSelected(account);
                          setRestoreOrgIdSelected(id);
                          setRestoreOrgBackupDates(backup_dates);
                          setRestoreSourceCountryCode(country_code || "");
                          setRestoreModalProvider(rowM);
                        }}
                        disabled={
                          restrict_options_except_delete == 1 ||
                          disable_vault_and_restore_option == 1
                        }
                      >
                        <ReactSVG src={"/assets/icons/arrow-clockwise.svg"} />
                        Restore
                      </Button>
                    )}
                    {org_status == 1 ? (
                      <>
                        <Button
                          className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                          onClick={() => {
                            openDowngradeModal(id, account, rowM);
                          }}
                          disabled={
                            restrict_options_except_delete == 1 ||
                            disable_vault_and_restore_option == 1
                          }
                        >
                          <ReactSVG src={"/assets/icons/folder.svg"} />
                          Downgrade to Vault
                        </Button>
                        {rowM === "qbo" && need_re_authentication != 1 && (
                          <Button
                            className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                            onClick={() => {
                              setOpenDropdownId(null);
                              setRevokeConnectionAccountName(account);
                              setRevokeConnectionModalOpen(true);
                              setSelectedOrgAction(id);
                              setSelectedOrgActionProvider(rowM);
                            }}
                          >
                            <ReactSVG src={"/assets/icons/cancel.svg"} />
                            Disconnect
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {is_vault_activated != 1 && (
                          <Button
                            className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                            onClick={() => {
                              openDowngradeModal(id, account, rowM);
                            }}
                            disabled={
                              restrict_options_except_delete == 1 ||
                              disable_vault_and_restore_option == 1
                            }
                          >
                            <ReactSVG src={"/assets/icons/folder.svg"} />
                            Downgrade to Vault
                          </Button>
                        )}
                        <Button
                          className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                          onClick={() => {
                            setOpenDropdownId(null);
                            openMultiSubscribeWizard(statusKey || id);
                          }}
                          disabled={
                            (restoreStatus[statusKey] != null &&
                              restoreStatus[statusKey]?.restoreStatus !=
                                "completed") ||
                            restrict_options_except_delete == 1
                          }
                        >
                          <ReactSVG
                            src={providerIconPath(rowM)}
                            className="provider-icon"
                          />
                          {is_vault_activated == 1
                            ? "Upgrade to Subscribe"
                            : "Subscribe"}
                        </Button>
                        {rowM === "qbo" && need_re_authentication != 1 && (
                          <Button
                            className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                            onClick={() => {
                              setOpenDropdownId(null);
                              setRevokeConnectionAccountName(account);
                              setRevokeConnectionModalOpen(true);
                              setSelectedOrgAction(id);
                              setSelectedOrgActionProvider(rowM);
                            }}
                          >
                            <ReactSVG src={"/assets/icons/cancel.svg"} />
                            Disconnect
                          </Button>
                        )}
                      </>
                    )}

                    {org_status == 1 && is_vault_activated != 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm text-gray-700 hover:text-blue-700"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setRetentionOrg({
                            ...item,
                            id,
                            account,
                            name: account,
                            provider: rowM,
                            country_code,
                          });
                          setRetentionModalOpen(true);
                        }}
                        disabled={restrict_options_except_delete == 1}
                      >
                        <Clock className="w-4 h-4 mr-2 text-blue-600" />
                        Extended Retention
                      </Button>
                    )}

                    {org_status == 1 && is_vault_activated != 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm text-gray-700 hover:text-indigo-700"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setManageAddonsOrg({
                            ...item,
                            id,
                            account,
                            name: account,
                            provider: rowM,
                            country_code,
                          });
                          setManageAddonsModalOpen(true);
                        }}
                        disabled={restrict_options_except_delete == 1}
                      >
                        <Layers className="w-4 h-4 mr-2 text-indigo-600" />
                        Manage Add-ons
                      </Button>
                    )}

                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null);
                        handleClearExistingBackup(id, rowM);
                      }}
                      disabled={restrict_options_except_delete == 1}
                    >
                      <ReactSVG src={"/assets/icons/broom.svg"} />
                      Clear Existing Backup Data
                    </Button>
                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null);
                        const nextPage = {
                          header: account,
                          page: organisationPage.ORGANISATION_FOLDER,
                        };
                        dispatch(addToNestedHeader(nextPage));
                        setNestedHeader(nextPage);
                        setBreadCrumb([]);
                        setBreadCrumb((prevState) => [
                          ...prevState,
                          { label: account, url: window.location.href },
                        ]);

                        onOrgIdChange(id, rowM);
                      }}
                      disabled={restrict_options_except_delete == 1}
                    >
                      <ReactSVG src={"/assets/icons/folder.svg"} />
                      Show Backups
                    </Button>

                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null);
                        setDeleteOrgModalOpen(true);
                        setDeleteOrgSelected(account);
                        setDeleteTargets([
                          { id: Number(id), provider: rowM },
                        ]);
                      }}
                      danger
                    >
                      <ReactSVG src={"/assets/icons/delete.svg"} />
                      Delete
                    </Button>
                    {org_status == 1 && is_vault_activated != 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setCancelSubscriptionModalOpen(true);
                          setSelectedOrgDetails({ id, provider: rowM, isVault: false });
                        }}
                        danger
                        disabled={isCancelSubDisabled || is_suscription_cancel_requested == 1 } // 🔒 disable on success
                      >
                        {cancelSubLoading ? (
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                        ) : (
                          <Ban className="w-4 h-4" />
                        )}
                        Cancel Subscription
                      </Button>
                    )}
                    {is_vault_activated == 1 && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setCancelSubscriptionModalOpen(true);
                          setSelectedOrgDetails({ id, provider: rowM, isVault: true });
                        }}
                        danger
                        disabled={isCancelSubDisabled || is_suscription_cancel_requested == 1 }
                      >
                        {cancelSubLoading ? (
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                        ) : (
                          <Ban className="w-4 h-4" />
                        )}
                        Cancel Vault Subscription
                      </Button>
                    )}
                    {restoreStatus[statusKey]?.restoreStatus === "completed" && restoreStatus[statusKey]?.restoreQueueId && (
                      <Button
                        className="w-full border-none flex justify-start shadow-none font-medium text-sm text-indigo-700 hover:text-indigo-800"
                        onClick={() => {
                          setOpenDropdownId(null);
                          navigate(`/restore-report/${rowM}/${restoreStatus[statusKey].restoreQueueId}`);
                        }}
                      >
                        <FileText className="w-4 h-4" />
                        View Restore Report
                      </Button>
                    )}
                    <Button
                      className="w-full border-none flex justify-start shadow-none font-medium text-sm"
                      onClick={() => {
                        setOpenDropdownId(null); // Close the dropdown
                        if (supportRef.current) {
                          supportRef.current.openWithOrganization(id, account);
                        }
                      }}
                    >
                      <ReactSVG src={"/assets/icons/support.svg"} />
                      Get Support
                    </Button>
                  </div>
                )}
              >
                <button
                  type="button"
                  className="p-0 border-0 bg-transparent cursor-pointer rounded-lg hover:bg-white inline-flex items-center justify-center"
                  aria-label={`Open actions for ${account}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ReactSVG
                    className="p-1 org-three-dots rounded-lg"
                    src={"/assets/icons/dot-menu.svg"}
                  />
                </button>
              </Dropdown>
            </div>
          </div>
        </div>
        {openedIndex == index && (
          <div className={"mt-2"}>
            <div className="flex justify-between">
              <div>Renewal Date:</div>
              <div>{item.subscription_renewal_date}</div>
            </div>
            <div className="flex justify-between mt-1">
              <div>Subscription:</div>
              <div>
                <div className="text-gray-500 flex gap-2 items-center">
                  {item.subscription == 1 &&
                  item.need_re_authentication == 1 ? (
                    <HelpCircle className="text-yellow-500 w-5 h-5" />
                  ) : item.subscription == 1 || item.is_vault_activated == 1 ? (
                    <CheckCircle className="text-green-500 w-5 h-5" />
                  ) : item.is_trial_expired == 1 ? (
                    <XCircle className="text-red-500 w-5 h-5" />
                  ) : null}
                  {getSubscriptionStatus(item)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  });

  const onDelete = () => {
    const selectedOrgs = filteredOrganizations.filter((org) =>
      selectedRowKeys.includes(org.statusKey)
    );
    setDeleteTargets(
      selectedOrgs.map((o) => ({
        id: Number(o.id),
        provider: orgProvider(o),
      }))
    );
    setDeleteOrgSelected(
      selectedOrgs.length === 1 ? selectedOrgs[0].account : null
    );
    setDeleteOrgModalOpen(true);
  };

  const onCancelSubscription = async () => {
    setCancelSubLoading(true); // start loading spinner

    try {
      const token = await getIdTokenClaims();

      const cancelSubApi =
        selectedOrgDetails?.provider === "qbo"
          ? cancelQboSubscription
          : cancelSubscription;

      cancelSubApi(token.__raw, selectedOrgDetails.id)
        .then((res) => {
          const modalTitle = selectedOrgDetails?.isVault
            ? "Vault Subscription Cancellation"
            : "Subscription Cancellation";
          if (res.data.success) {
            setIsCancelSubDisabled(true); // disable button on success
            setSuccessTitle(modalTitle);
            setSuccessText(res.data.message);
            setSuccessModalOpen(true);
            fetchOrgs();
          } else {
            setSuccessTitle(modalTitle);
            setSuccessText(res.data.message);
            setSuccessModalOpen(true);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } catch (e) {
      console.error(e);
    } finally {
      setCancelSubLoading(false);
      setCancelSubscriptionModalOpen(false);
    }
  };

  // QBO-only actions (currently disabled; keep for later)
  /*
  const handleQboSync = async (orgId) => {
    try {
      setTableDataLoading(true);
      const token = await getIdTokenClaims();
      const res = await qboSync(token.__raw, orgId);

      const success = res?.data?.success !== false;
      enqueueSnackbar(res?.data?.message || "Sync request submitted.", {
        variant: success ? "success" : "error",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to sync. Please try again.", {
        variant: "error",
      });
    } finally {
      setTableDataLoading(false);
      fetchOrgs();
    }
  };

  const submitQboReassign = async () => {
    const orgId = qboReassignOrg?.id;
    const userIdNum = Number(qboReassignUserId);
    if (!orgId || !Number.isFinite(userIdNum) || userIdNum <= 0) {
      enqueueSnackbar("Please enter a valid numeric user id.", {
        variant: "error",
      });
      return;
    }

    try {
      setQboReassignLoading(true);
      const token = await getIdTokenClaims();
      const res = await qboReassign(token.__raw, orgId, userIdNum);

      const success = res?.data?.success !== false;
      enqueueSnackbar(res?.data?.message || "Reassign request submitted.", {
        variant: success ? "success" : "error",
      });

      if (success) {
        setQboReassignModalOpen(false);
        setQboReassignOrg(null);
        setQboReassignUserId("");
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to reassign. Please try again.", {
        variant: "error",
      });
    } finally {
      setQboReassignLoading(false);
      fetchOrgs();
    }
  };
  */

  const eligibleSubscribeOrgs = useMemo(
    () => organizations.filter((org) => isOrgEligibleForSubscribe(org)),
    [organizations]
  );

  const resetMultiSubscribeWizard = () => {
    setMultiSubscribeOpen(false);
    setMultiSubscribeOrgs([]);
    setMultiSubscribePlans({});
    setMultiSubscribeStepIndex(0);
    setMultiSubscribeShowSummary(false);
    setMultiSubscribeConfirming(false);
  };

  const openDowngradeModal = async (id, account, rowProv) => {
    setOpenDropdownId(null);
    setVaultDowngradeAccountName(account);
    setSelectedOrgAction(id);
    setSelectedOrgActionProvider(rowProv);
    setDisconnectCouponCode("");
    setDisconnectCouponDiscount(null);
    try {
      const token = await getIdTokenClaims();
      const defaultMethodRes = await getDefaultMethod(token.__raw);
      const hasDefault =
        defaultMethodRes.data?.success !== false &&
        !!defaultMethodRes.data?.default_method;
      setHasDefaultPaymentMethod(hasDefault);
    } catch (err) {
      console.warn("Could not check default payment method:", err);
    }
    setRemoveOrgModalOpen(true);
  };

  const openMultiSubscribeWizard = async (targetOrgId = null, orgsList = null) => {
    const rawOrgs = orgsList || organizations;

    const singleTargetId =
      typeof targetOrgId === "string" || typeof targetOrgId === "number"
        ? targetOrgId
        : null;

    let orderedEligible = [];
    if (Array.isArray(targetOrgId) && targetOrgId.length > 0) {
      const targetSet = new Set(targetOrgId.map(String));
      orderedEligible = rawOrgs.filter(
        (o) =>
          (targetSet.has(String(o.id)) ||
            targetSet.has(getOrgKey(o)) ||
            targetSet.has(String(o.statusKey))) &&
          isOrgEligibleForSubscribe(o)
      );
    } else if (singleTargetId != null) {
      const singleStr = String(singleTargetId);
      const found = rawOrgs.find(
        (o) =>
          (String(o.id) === singleStr ||
            getOrgKey(o) === singleStr ||
            String(o.statusKey) === singleStr) &&
          isOrgEligibleForSubscribe(o)
      );
      orderedEligible = found ? [found] : [];
    } else if (selectedRowKeys.length > 0) {
      const targetSet = new Set(selectedRowKeys.map(String));
      orderedEligible = rawOrgs.filter(
        (o) =>
          (targetSet.has(String(o.id)) ||
            targetSet.has(getOrgKey(o)) ||
            targetSet.has(String(o.statusKey))) &&
          isOrgEligibleForSubscribe(o)
      );
    } else {
      orderedEligible = rawOrgs.filter(isOrgEligibleForSubscribe);
    }

    if (orderedEligible.length === 0) {
      enqueueSnackbar(
        "No eligible organizations selected to subscribe or manage add-ons.",
        { variant: "info" }
      );
      return;
    }

    try {
      const token = await getIdTokenClaims();
      const [methodsRes, defaultRes] = await Promise.all([
        getPaymentMethods(token.__raw),
        getDefaultMethod(token.__raw),
      ]);
      const cardsList = methodsRes.data?.cards?.data || [];
      const defaultId = defaultRes.data?.default_method || null;
      setPaymentCards(cardsList);
      setDefaultPaymentMethodId(defaultId);
      setHasDefaultPaymentMethod(cardsList.length > 0);
      if (defaultId && cardsList.some((c) => c.id === defaultId)) {
        setSelectedPaymentMethodId(defaultId);
      } else if (cardsList.length > 0) {
        setSelectedPaymentMethodId(cardsList[0].id);
      } else {
        setSelectedPaymentMethodId(null);
      }
    } catch (err) {
      console.warn("Could not fetch payment methods:", err);
    }

    setMultiSubscribeOrgs(orderedEligible);
    setMultiSubscribePlans(buildInitialOrgPlans(orderedEligible, targetOrgId));
    setMultiSubscribeStepIndex(0);
    setMultiSubscribeShowSummary(false);
    setMultiSubscribeOpen(true);
  };

  const handleMultiSubscribePlanChange = (orgKey, patch) => {
    setMultiSubscribePlans((prev) => {
      const current = prev[orgKey] ?? getDefaultOrgPlan();
      const updated = {
        ...current,
        ...patch,
      };
      if (patch.accounts === false) {
        updated.files = false;
        updated.history_and_notes = false;
        updated.payroll = false;
        updated.skipped = true;
      } else if (patch.accounts === true) {
        updated.skipped = false;
      }
      return {
        ...prev,
        [orgKey]: updated,
      };
    });
  };

  const handleMultiSubscribeSkipOrg = (orgKey) => {
    setMultiSubscribePlans((prev) => ({
      ...prev,
      [orgKey]: {
        ...(prev[orgKey] || getDefaultOrgPlan()),
        skipped: true,
        accounts: false,
        files: false,
        history_and_notes: false,
        payroll: false,
      },
    }));
    if (multiSubscribeStepIndex < multiSubscribeOrgs.length - 1) {
      setMultiSubscribeStepIndex((i) => i + 1);
    } else {
      setMultiSubscribeShowSummary(true);
    }
  };

  const handleMultiSubscribeSkipAll = () => {
    setMultiSubscribePlans((prev) => {
      const updated = { ...prev };
      multiSubscribeOrgs.forEach((org) => {
        const key = getOrgKey(org);
        updated[key] = {
          ...(updated[key] || getDefaultOrgPlan()),
          skipped: true,
          accounts: false,
          files: false,
          history_and_notes: false,
          payroll: false,
        };
      });
      return updated;
    });
  };

  const handleMultiSubscribeIncludeAll = () => {
    setMultiSubscribePlans((prev) => {
      const updated = { ...prev };
      multiSubscribeOrgs.forEach((org) => {
        const key = getOrgKey(org);
        updated[key] = {
          ...(updated[key] || getDefaultOrgPlan()),
          skipped: false,
          accounts: true,
        };
      });
      return updated;
    });
  };

  const handleMultiSubscribeApplyCouponToAll = (couponCode, couponDiscount) => {
    setMultiSubscribePlans((prev) => {
      const updated = { ...prev };
      multiSubscribeOrgs.forEach((org) => {
        const key = getOrgKey(org);
        const current = updated[key] || getDefaultOrgPlan();
        if (!current.skipped && current.accounts) {
          updated[key] = {
            ...current,
            coupon_code: couponCode,
            coupon_discount: couponDiscount,
          };
        }
      });
      return updated;
    });
    enqueueSnackbar("Coupon applied to all active organizations.", {
      variant: "success",
    });
  };

  const handleMultiSubscribeRemoveCouponFromAll = () => {
    setMultiSubscribePlans((prev) => {
      const updated = { ...prev };
      multiSubscribeOrgs.forEach((org) => {
        const key = getOrgKey(org);
        const current = updated[key] || getDefaultOrgPlan();
        updated[key] = {
          ...current,
          coupon_code: "",
          coupon_discount: null,
        };
      });
      return updated;
    });
  };

  const handleMultiSubscribeNext = () => {
    if (multiSubscribeStepIndex >= multiSubscribeOrgs.length - 1) {
      setMultiSubscribeShowSummary(true);
      return;
    }
    setMultiSubscribeStepIndex((i) => i + 1);
  };

  const handleMultiSubscribeOrgSelect = (orgKey) => {
    if (orgKey === "summary") {
      setMultiSubscribeShowSummary(true);
      return;
    }
    const index = multiSubscribeOrgs.findIndex(
      (org) =>
        getOrgKey(org) === String(orgKey) || String(org.id) === String(orgKey)
    );
    if (index >= 0) {
      setMultiSubscribeStepIndex(index);
      setMultiSubscribeShowSummary(false);
    }
  };

  const handleMultiSubscribeApplySettingsToAll = (templatePlan) => {
    setMultiSubscribePlans((prev) => {
      const updated = { ...prev };
      multiSubscribeOrgs.forEach((org) => {
        const orgProvider = org.provider ?? "xero";
        const key = getOrgKey(org);
        const current = updated[key] || getDefaultOrgPlan(org);
        if (!current.skipped && current.accounts) {
          updated[key] = {
            ...current,
            files: orgProvider === "qbo" ? false : !!templatePlan.files,
            history_and_notes:
              orgProvider === "qbo" ? false : !!templatePlan.history_and_notes,
            payroll:
              orgProvider === "qbo"
                ? false
                : !!templatePlan.payroll && isPayrollSubscribeEligible(org.country_code),
            retention_days: Number(templatePlan.retention_days) || 7,
          };
        }
      });
      return updated;
    });
    enqueueSnackbar(
      "Applied module & retention settings to all selected organizations.",
      { variant: "success" }
    );
  };

  const handleMultiSubscribeConfirm = async (chosenCardId) => {
    const included = multiSubscribeOrgs.filter((org) => {
      const key = getOrgKey(org);
      const plan = multiSubscribePlans[key] ?? multiSubscribePlans[org.id];
      return plan && !plan.skipped && plan.accounts;
    });

    if (included.length === 0) {
      enqueueSnackbar("Select at least one organization to subscribe or update.", {
        variant: "info",
      });
      return;
    }

    // Separate into new subscriptions vs existing subscriptions
    const newSubscribeEntries = [];
    const updatedExistingEntries = [];
    const unchangedExistingCount = [];

    included.forEach((org) => {
      const key = getOrgKey(org);
      const plan = multiSubscribePlans[key] ?? multiSubscribePlans[org.id] ?? getDefaultOrgPlan(org);
      const isExisting = Boolean(
        Number(org.org_status) === 1 || Number(org.subscription) === 1 || plan.isExistingSubscription
      );

      if (isExisting) {
        if (hasPlanAddonsChanged(plan)) {
          updatedExistingEntries.push({ org, plan });
        } else {
          // If there is no change on it, leave it as the previous one
          unchangedExistingCount.push(org);
        }
      } else {
        newSubscribeEntries.push(org);
      }
    });

    const effectiveCardId =
      chosenCardId || selectedPaymentMethodId || defaultPaymentMethodId;

    if (newSubscribeEntries.length > 0 && (paymentCards.length === 0 || !effectiveCardId)) {
      dispatch(setAddCardModalOpen(true));
      return;
    }

    setMultiSubscribeConfirming(true);

    try {
      const token = await getIdTokenClaims();
      const requests = [];

      // 1. Create subscriptions for new organizations
      if (newSubscribeEntries.length > 0) {
        const payload = buildBulkSubscribePayload(
          newSubscribeEntries,
          multiSubscribePlans
        );

        const xeroPayload = payload.filter((entry) => entry.provider === "xero");
        const qboPayload = payload.filter((entry) => entry.provider === "qbo");

        if (xeroPayload.length > 0) {
          requests.push(
            bulkSubscribeXeroWithPlans(token.__raw, xeroPayload, effectiveCardId)
          );
        }
        if (qboPayload.length > 0) {
          requests.push(
            bulkSubscribeQboWithPlans(token.__raw, qboPayload, effectiveCardId)
          );
        }
      }

      // 2. Update add-on modules and retention for existing subscriptions that changed
      const addonUpdatePromises = [];
      updatedExistingEntries.forEach(({ org, plan }) => {
        const init = plan.initialState || {};
        const addonsChanged =
          Boolean(plan.files) !== Boolean(init.files) ||
          Boolean(plan.history_and_notes) !== Boolean(init.history_and_notes) ||
          Boolean(plan.payroll) !== Boolean(init.payroll);
        const retentionChanged =
          Number(plan.retention_days || 7) !== Number(init.retention_days || 7);

        if (addonsChanged) {
          addonUpdatePromises.push(
            updateSubscriptionAddons(token.__raw, {
              org_id: org.id,
              provider: org.provider ?? "xero",
              files: !!plan.files,
              history_and_notes: !!plan.history_and_notes,
              payroll: !!plan.payroll,
            })
          );
        }

        if (retentionChanged) {
          addonUpdatePromises.push(
            updateBackupRetention(token.__raw, {
              org_id: org.id,
              provider: org.provider ?? "xero",
              days: Number(plan.retention_days || 7),
            })
          );
        }
      });

      const [bulkResults, addonResults] = await Promise.all([
        Promise.all(requests),
        Promise.all(addonUpdatePromises),
      ]);

      const subscribed = bulkResults.flatMap((res) => res.data?.subscribed ?? []);
      const failed = bulkResults.flatMap((res) => res.data?.failed ?? []);
      const anySuccess = bulkResults.length === 0 ? true : bulkResults.some((res) => res.data?.success);
      const addonSuccessCount = addonResults.filter((r) => r.data?.success).length;

      const redirectUrl = bulkResults.find((res) => res.data?.redirect_url)?.data?.redirect_url;

      resetMultiSubscribeWizard();
      removeDataFromTable();
      fetchOrgs();

      if (anySuccess && redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      const summaryParts = [];
      if (subscribed.length > 0) {
        summaryParts.push(`${subscribed.length} organization(s) newly subscribed.`);
      }
      if (addonSuccessCount > 0) {
        summaryParts.push(`${addonSuccessCount} organization add-on(s) updated.`);
      }
      if (unchangedExistingCount.length > 0) {
        summaryParts.push(`${unchangedExistingCount.length} existing organization(s) kept as-is.`);
      }

      setSuccessModalOpen(true);
      setSuccessTitle("Subscriptions & Add-ons Processed");
      setSuccessText(
        summaryParts.join(" ") || "Organization plans processed successfully."
      );
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          "Something went wrong while processing subscriptions.",
        { variant: "error" }
      );
    } finally {
      setMultiSubscribeConfirming(false);
    }
  };

  const hasNoOrganizations =
    !isLoading && !tableDataLoading && organizations.length === 0;
  const showOrgBillingNotice =
    hasNoOrganizations && !billingEmptyNoticeDismissed;

  const renderOrganizationsEmptyContent = () => (
    <div className="organizations-table-empty-message flex flex-col items-center justify-center gap-5 py-8">
      {showOrgBillingNotice && (
        <div className="organizations-table-empty-billing org-tour-billing w-full max-w-lg">
          <BillingWelcomeNotice
            variant="compact"
            className="w-full"
            onDismiss={() => setBillingEmptyNoticeDismissed(true)}
          />
        </div>
      )}
      <p className="m-0 text-sm font-medium text-gray-600 text-center">
        No organizations added
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <QuickBooksConnectButton onClick={() => handleProviderSelect("qbo")} />
        <AddButton
          text="Add Other"
          clickMethod={handleAddNewOrg}
          className="add-org-btn"
        />
      </div>
    </div>
  );

  const getTableEmptyText = () => {
    if (isLoading || tableDataLoading) {
      return "No Data";
    }
    if (hasNoOrganizations) {
      return renderOrganizationsEmptyContent();
    }
    if (filteredOrganizations.length === 0) {
      return "No organizations match your filters.";
    }
    return "No Data";
  };

  return (
    <div className="relative org-tour-main-area">
      {/* Blur overlay when loading */}
      <div
        className={`${
          addOrgLoading ? "blur-sm pointer-events-none" : ""
        } transition-all duration-200`}
      >
        <div>
          {/* Banner shown when arriving from Intuit Disconnect flow */}
          {showDisconnectedBanner && (
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-gray-900">
                    QuickBooks connection disconnected
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Your QuickBooks Online organization has been disconnected. You can connect it again at any time.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <QuickBooksConnectButton onClick={() => handleProviderSelect("qbo")} />
                <button
                  type="button"
                  onClick={() => setShowDisconnectedBanner(false)}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row lg:items-center gap-3 my-4">
        {/* Bulk Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onDelete}
            disabled={!selectedRowKeys.length}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border ${
              selectedRowKeys.length
                ? "text-gray-700 border-gray-300 hover:bg-gray-50"
                : "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
            }`}
          >
            <ReactSVG src="/assets/icons/delete.svg" />
            Delete
          </button>
          <button
            onClick={() => {
              if (!selectedRowKeys.length) return;
              const selectedOrgsList = organizations.filter(
                (o) =>
                  selectedRowKeys.includes(o.statusKey) ||
                  selectedRowKeys.includes(o.id) ||
                  selectedRowKeys.includes(String(o.id))
              );
              const selectedEligible = selectedOrgsList.filter(isOrgEligibleForSubscribe);
              if (selectedEligible.length === 0) {
                enqueueSnackbar(
                  "Please select organizations that do not require re-authentication.",
                  { variant: "warning" }
                );
                return;
              }
              openMultiSubscribeWizard(selectedRowKeys, selectedEligible);
            }}
            disabled={!selectedRowKeys.length}
            className={`org-tour-bulk-subscribe flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border ${
              selectedRowKeys.length
                ? "text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer"
                : "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
            }`}
          >
            <ReactSVG
              src="/assets/icons/organization.svg"
              className="provider-icon"
            />
            Subscribe{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ""}
          </button>
          {role === "superadmin" && (
            <button
              onClick={() => {
                const selectedOrgsList = organizations.filter(
                  (o) =>
                    selectedRowKeys.includes(o.statusKey) ||
                    selectedRowKeys.includes(o.id) ||
                    selectedRowKeys.includes(String(o.id))
                );
                setAdminManualOrg(null);
                setAdminManualOrgs(selectedOrgsList);
                setAdminManualModalOpen(true);
              }}
              disabled={!selectedRowKeys.length}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border ${
                selectedRowKeys.length
                  ? "text-blue-700 border-blue-300 hover:bg-blue-50 bg-blue-50/50"
                  : "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed"
              }`}
              title="Bulk manage manual / offline billing for selected companies"
            >
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              Offline Billing {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
            </button>
          )}
        </div>

        {/* Search + filters: wide row avoids cramped selects (Subscription / Platform / Backups) */}
        <div className="org-tour-filters flex min-w-0 flex-1 flex-col gap-2 rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm sm:flex-row sm:flex-wrap sm:items-center">
          {/* Search Type Dropdown - Only visible for Admin (superadmin) */}
          {role === "superadmin" && (
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="h-9 w-full min-w-0 shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 text-sm outline-none sm:w-44"
            >
              <option value="org">Organization Name </option>
              <option value="owner">Owner</option>
            </select>
          )}
          <div className="relative flex-1 flex items-center min-w-[10rem] sm:min-w-[12rem]">
            <input
              type="text"
              placeholder={
                role === "superadmin" && searchType === "owner"
                  ? "Search by owner..."
                  : "Search orgs..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 w-full rounded-md border border-gray-200 bg-white pl-3 pr-8 outline-none placeholder:text-gray-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                title="Clear search"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>

          <select
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value)}
            className="h-9 w-full min-w-[11rem] shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 text-sm outline-none sm:w-44"
          >
            <option value="">All subscriptions</option>
            <option value="Vault">Vault</option>
            <option value="Trial">Trial</option>
            <option value="Subscribed">Subscribed</option>
            {combinedMode ? (
              <>
                <option value="Connect with Xero">Connect Xero</option>
                <option value="Connect with QuickBooks">
                  Connect QuickBooks
                </option>
                <option value="Reconnect with Xero">Reconnect Xero</option>
              </>
            ) : (
              <>
                <option
                  value={`Connect with ${providerLabel(
                    type === "qbo" ? "qbo" : "xero"
                  )}`}
                >
                  Connect {providerLabel(type === "qbo" ? "qbo" : "xero")}
                </option>
                {type !== "qbo" && (
                  <option
                    value={`Reconnect with ${providerLabel("xero")}`}
                  >
                    Reconnect {providerLabel("xero")}
                  </option>
                )}
              </>
            )}
            <option value="Trial Expired">Trial Expired</option>
            <option value="Not Subscribed">Not Subscribed</option>
          </select>

          {combinedMode && (
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="h-9 w-full min-w-[11rem] shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 text-sm outline-none sm:w-40"
            >
              <option value="">All platforms</option>
              <option value="xero">Xero</option>
              <option value="qbo">QuickBooks</option>
            </select>
          )}

          <select
            value={backupFilter}
            onChange={(e) => setBackupFilter(e.target.value)}
            className="h-9 w-full min-w-[11rem] shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 text-sm outline-none sm:w-44"
            aria-label="Filter by backup status"
          >
            <option value="">All backup statuses</option>
            <option value="Pending">Pending</option>
            <option value="In progress">In progress</option>
            <option value="Completed">Completed</option>
            <option value="Paused">Paused</option>
            <option value="N/A">N/A</option>
          </select>
        </div>

        {/* Action Buttons - Right Side */}
        <div className="flex gap-2 lg:ml-auto">
		  {/* <button
            onClick={handleQBDToXero}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
          >
            QBD to Xero
          </button> */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/monthly-report")}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FileText className="w-4 h-4" />
              <span>Monthly Report</span>
            </button>
            <AddButton
              text="Add"
              clickMethod={handleAddNewOrg}
              className="add-org-btn"
            />
          </div>
        </div>
      </div>

      <Modal
        title={[<div className={"text-2xl"}>Add Xero Account</div>]}
        centered
        open={addAccModalOpen}
        footer={[
          <div className={"flex items-center justify-end gap-3"}>
            <div
              key="cancel"
              className={
                "px-2 text-sm py-1.5 border border-gray-200 rounded-lg"
              }
              onClick={() => setAddAccModalOpen(false)}
            >
              Cancel
            </div>

            <div
              key="submit"
              className={
                "px-2 text-sm py-1.5 bg-blue-500 text-white rounded-lg"
              }
              onClick={() => handleAddNewOrg}
            >
              Confirm
            </div>
          </div>,
        ]}
        onOk={() => setAddAccModalOpen(false)}
        onCancel={() => setAddAccModalOpen(false)}
      >
        <AddAccountModal />
      </Modal>

      <SuccessModal
        title={successTitle}
        text={successText}
        modalOpen={successModalOpen}
        onModalClose={successModalCloseHandler}
      />

      <DisconnectOrgModal
        open={removeOrgModalOpen}
        onCancel={() => {
          setRemoveOrgModalOpen(false);
          setDisconnectCouponCode("");
          setDisconnectCouponDiscount(null);
        }}
        onConfirm={() => {
          handleDisconnected(disconnectCouponCode);
        }}
        loading={disconnectLoading}
        organizationName={vaultDowngradeAccountName}
        provider={selectedOrgActionProvider}
        hasDefaultPaymentMethod={hasDefaultPaymentMethod}
        couponCode={disconnectCouponCode}
        onCouponCodeChange={setDisconnectCouponCode}
        appliedDiscount={disconnectCouponDiscount}
        onDiscountApplied={setDisconnectCouponDiscount}
      />

      <Modal
        title={[
          <div key="title" className={"text-2xl"}>
            Disconnect Organization
          </div>,
        ]}
        centered
        open={revokeConnectionModalOpen}
        footer={[
          <div key="footer" className={"flex items-center justify-end gap-3"}>
            <div
              key="cancel"
              className={
                "px-2 text-sm py-1.5 border border-gray-200 rounded-lg cursor-pointer"
              }
              onClick={() => setRevokeConnectionModalOpen(false)}
            >
              Cancel
            </div>

            <div
              key="submit"
              className={
                "px-2 text-sm py-1.5 bg-blue-500 text-white rounded-lg cursor-pointer"
              }
              onClick={() => {
                setRevokeConnectionModalOpen(false);
                handleRevokeConnection();
              }}
            >
              Disconnect
            </div>
          </div>,
        ]}
        onOk={() => setRevokeConnectionModalOpen(false)}
        onCancel={() => setRevokeConnectionModalOpen(false)}
      >
        <RevokeConnectionModal
          organizationName={revokeConnectionAccountName}
          provider={selectedOrgActionProvider}
        />
      </Modal>

      <Modal
        title={[
          <div key="title" className="text-xl font-semibold text-gray-900">
            Connect to QuickBooks
          </div>,
        ]}
        centered
        open={connectQboModalOpen}
        footer={null}
        onCancel={() => setConnectQboModalOpen(false)}
      >
        <div className="py-4 text-center">
          <p className="text-sm text-gray-600 mb-6">
            Connect{" "}
            <span className="font-semibold text-gray-900">
              {connectQboModalAccountName}
            </span>{" "}
            with QuickBooks Online to sync your company data and resume automated backups.
          </p>
          <div className="flex justify-center">
            <QuickBooksConnectButton
              onClick={() => {
                setConnectQboModalOpen(false);
                handleProviderSelect("qbo");
              }}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title={[<div className={"text-2xl"}>Unsubscribe organization</div>]}
        centered
        open={unsubOrgModalOpen}
        footer={[
          <div className={"flex items-center justify-end gap-3"}>
            <div
              key="cancel"
              className={
                "px-2 text-sm py-1.5 border border-gray-200 rounded-lg"
              }
              onClick={() => setUnsubOrgModalOpen(false)}
            >
              Cancel
            </div>

            <div
              key="submit"
              className={
                "px-2 text-sm py-1.5 bg-blue-500 text-white rounded-lg cursor-pointer"
              }
              onClick={() => {
                setUnsubOrgModalOpen(false);
                handleSubscribeUpdate();
              }}
            >
              Confirm
            </div>
          </div>,
        ]}
        onOk={() => {
          setUnsubOrgModalOpen(false);
          handleSubscribeUpdate();
        }}
        onCancel={() => setUnsubOrgModalOpen(false)}
      >
        <div className={"py-8 text-gray-500 text-sm border-b border-gray-300"}>
          <div>Are you sure you want to unsubscribe the organization?</div>
        </div>
      </Modal>

      <SendXeroReauthEmailModal
        modalStatus={sendReauthModalOpen}
        setModalStatus={setSendReauthModalOpen}
        organizationId={reauthOrgId}
        organizationName={reauthOrgName}
        initialEmail={reauthOrgEmail}
        provider={reauthOrgProvider}
        onModalClose={() => {
          setReauthOrgId(null);
          setReauthOrgName("");
          setReauthOrgEmail("");
          setReauthOrgProvider("xero");
        }}
      />

      <SendXeroSubscriptionEmailModal
        modalStatus={sendSubscriptionModalOpen}
        setModalStatus={setSendSubscriptionModalOpen}
        organizationId={subscriptionOrgId}
        organizationName={subscriptionOrgName}
        initialEmail={subscriptionOrgEmail}
        provider={subscriptionOrgProvider}
        onModalClose={() => {
          setSubscriptionOrgId(null);
          setSubscriptionOrgName("");
          setSubscriptionOrgEmail("");
          setSubscriptionOrgProvider("xero");
        }}
      />

      <AdminManualSubscriptionModal
        modalStatus={adminManualModalOpen}
        setModalStatus={(open) => {
          setAdminManualModalOpen(open);
          if (!open) {
            setAdminManualOrg(null);
            setAdminManualOrgs([]);
          }
        }}
        organization={adminManualOrg}
        organizations={adminManualOrgs}
        onSuccess={() => {
          setSelectedRowKeys([]);
          setAdminManualOrgs([]);
          fetchOrgs();
        }}
      />

      <AdminBulkApiModal
        modalStatus={adminBulkApiModalOpen}
        setModalStatus={setAdminBulkApiModalOpen}
        organization={adminBulkApiOrg}
        onSuccess={() => {
          fetchOrgs();
        }}
      />

      <ExtendRetentionModal
        open={retentionModalOpen}
        onClose={() => {
          setRetentionModalOpen(false);
          setRetentionOrg(null);
        }}
        organization={retentionOrg}
        onSuccess={() => {
          fetchOrgs();
        }}
        paymentCards={paymentCards}
        defaultPaymentMethodId={defaultPaymentMethodId}
        onAddNewCard={() => dispatch(setAddCardModalOpen(true))}
      />

      <ManageAddonsModal
        open={manageAddonsModalOpen}
        onClose={() => {
          setManageAddonsModalOpen(false);
          setManageAddonsOrg(null);
        }}
        organization={manageAddonsOrg}
        onSuccess={() => {
          fetchOrgs();
        }}
        paymentCards={paymentCards}
        defaultPaymentMethodId={defaultPaymentMethodId}
        onAddNewCard={() => dispatch(setAddCardModalOpen(true))}
      />

      <MultiSubscribeWizardModal
        open={multiSubscribeOpen}
        onClose={resetMultiSubscribeWizard}
        orgs={multiSubscribeOrgs}
        orgPlans={multiSubscribePlans}
        currentStepIndex={multiSubscribeStepIndex}
        showSummary={multiSubscribeShowSummary}
        paymentCards={paymentCards}
        defaultPaymentMethodId={defaultPaymentMethodId}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onPaymentMethodChange={setSelectedPaymentMethodId}
        onAddNewCard={() => dispatch(setAddCardModalOpen(true))}
        onOrgSelect={handleMultiSubscribeOrgSelect}
        onPlanChange={handleMultiSubscribePlanChange}
        onSkipOrg={handleMultiSubscribeSkipOrg}
        onSkipAll={handleMultiSubscribeSkipAll}
        onIncludeAll={handleMultiSubscribeIncludeAll}
        onApplySettingsToAll={handleMultiSubscribeApplySettingsToAll}
        onApplyCouponToAll={handleMultiSubscribeApplyCouponToAll}
        onRemoveCouponFromAll={handleMultiSubscribeRemoveCouponFromAll}
        onNext={handleMultiSubscribeNext}
        onBack={() => setMultiSubscribeShowSummary(false)}
        onConfirm={handleMultiSubscribeConfirm}
        confirming={multiSubscribeConfirming}
        hasDefaultPaymentMethod={hasDefaultPaymentMethod}
      />

      <Elements stripe={stripePromise}>
        <AddCardModal
          makeDefaultPaymentMethod={() => {}}
          onCreate={async (newPaymentMethodId) => {
            try {
              const token = await getIdTokenClaims();
              const [methodsRes, defaultRes] = await Promise.all([
                getPaymentMethods(token.__raw),
                getDefaultMethod(token.__raw),
              ]);
              const cardsList = methodsRes.data?.cards?.data || [];
              const defaultId = defaultRes.data?.default_method || null;
              setPaymentCards(cardsList);
              setDefaultPaymentMethodId(defaultId);
              setHasDefaultPaymentMethod(cardsList.length > 0);

              const cardToSelect =
                typeof newPaymentMethodId === "string" && newPaymentMethodId.startsWith("pm_")
                  ? newPaymentMethodId
                  : (cardsList.length > 0 ? cardsList[0].id : defaultId);

              if (cardToSelect) {
                setSelectedPaymentMethodId(cardToSelect);
              }
            } catch (e) {
              console.error(e);
            }
          }}
        />
      </Elements>

      <AddRestoreOrgModal
        organizationId={restoreOrgIdSelected}
        organizationName={restoreOrgSelected}
        organizations={organizations}
        backupDates={restoreOrgBackupDates}
        onModalClose={onRestoreModalClose}
        onRestoreSubmit={handleRestoreSubmit}
        initialBackupDate={restorePreFill.initialBackupDate}
        initialTargetOrgId={restorePreFill.initialTargetOrgId}
        initialTargetOrgName={restorePreFill.initialTargetOrgName}
        skipVideo={restorePreFill.skipVideo}
        type={restoreModalProvider}
        sourceOrgCountryCode={restoreSourceCountryCode}
      />

      <DeleteOrgModal
        deleteTargets={deleteTargets}
        organizationName={deleteOrgSelected}
        modalStatus={deleteOrgModalOpen}
        setModalStatus={setDeleteOrgModalOpen}
        onModalClose={() => {
          setDeleteTargets(null);
          setDeleteOrgModalOpen(false);
        }}
        onSuccess={handleOrgDeleted}
      />
      <ConfirmModal
        title={confirmTitle}
        text={confirmText}
        modalOpen={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onOkay={confirmOnOkay}
      />
      {/* for cancelling subscription */}
      <ConfirmModal
        title={selectedOrgDetails?.isVault ? "Cancel Vault Subscription?" : "Unsubscribe from Auto Backup?"}
        text={
          selectedOrgDetails?.isVault
            ? `Your Vault subscription will remain active until the end of your current billing cycle.
Archived backup data will be retained during your current billing cycle.
You won't be charged again unless you re-subscribe.`
            : `Your subscription will remain active until the end of your current billing cycle.
Auto backups will continue during your current billing cycle.
You won't be charged again unless you re-subscribe.
`
        }
        subText={selectedOrgDetails?.isVault ? "Are you sure you want to cancel your Vault subscription?" : "Are you sure you want to unsubscribe?"}
        modalOpen={cancelSubscriptionModalOpen}
        onCancel={() => setCancelSubscriptionModalOpen(false)}
        onOkay={onCancelSubscription}
        confirmButtonText={"Confirm"}
      />

      {/* QBD to Xero Conversion Service Modal */}
      <Modal
        open={qbdToXeroModalOpen}
        onCancel={() => {
          setQbdToXeroModalOpen(false);
          setPaymentError(null);
        }}
        footer={null}
        width={600}
        closeIcon={<div className="text-gray-400 hover:text-gray-600">✕</div>}
      >
        <div className="py-4">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              <span className="text-green-600">QuickBooks Desktop</span>
              <span className="text-gray-400 mx-2">→</span>
              <span className="text-blue-500">Xero</span>
              <span className="text-gray-700"> Conversion Service</span>
            </h2>
            <p className="text-sm text-gray-500">
              Professional conversion service with 1 business day turnaround
            </p>
          </div>

          {/* Pricing Card */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 text-2xl font-bold">$</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">${getCurrentPrice()} USD per conversion</div>
                  <div className="text-sm text-gray-500">One-time payment</div>
                </div>
              </div>
              <span className="bg-cyan-400 text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                ⏱ 1 Business Day
              </span>
            </div>

            {/* Xero Organization Setup Options */}
            <div className="pt-4 border-t border-gray-200 mb-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">Xero Organization Setup</div>
              <div className="space-y-3">
                {/* New Organization Option */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="xeroOrgSetup"
                    value="new"
                    checked={xeroOrgSetupOption === 'new'}
                    onChange={(e) => setXeroOrgSetupOption(e.target.value)}
                    className="mt-1 w-4 h-4 text-blue-500 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      Create new Xero organizations (${QBD_XERO_PRICING.NEW_ORG}/conversion)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      We'll create new Xero organizations and migrate your data
                    </div>
                  </div>
                </label>

                {/* Existing Organization Option */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="xeroOrgSetup"
                    value="existing"
                    checked={xeroOrgSetupOption === 'existing'}
                    onChange={(e) => setXeroOrgSetupOption(e.target.value)}
                    className="mt-1 w-4 h-4 text-blue-500 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      Use your existing Xero organizations (${QBD_XERO_PRICING.EXISTING_ORG}/conversion)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      You provide Xero organizations for migration
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Migration Counter */}
            <div className="flex items-end justify-between pt-4 border-t border-gray-200">
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-600">Number of Migrations</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={decrementMigrations}
                    className="w-10 h-10 border-2 border-gray-300 rounded text-gray-600 hover:bg-gray-100 flex items-center justify-center font-bold text-lg"
                  >
                    -
                  </button>
                  <div className="w-20 h-10 border-2 border-blue-500 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-semibold">{numberOfMigrations}</span>
                  </div>
                  <button
                    onClick={incrementMigrations}
                    className="w-10 h-10 border-2 border-gray-300 rounded text-gray-600 hover:bg-gray-100 flex items-center justify-center font-bold text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500 mb-1">Total Price</div>
                <div className="text-2xl font-bold text-green-600">${numberOfMigrations * getCurrentPrice()} USD</div>
              </div>
            </div>
          </div>

          {/* How to Export Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-green-600 text-lg">📄</span>
              <h3 className="font-semibold text-gray-900">How to Export Your QuickBooks Desktop File</h3>
            </div>
            <div className="space-y-2">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                  1
                </div>
                <div className="text-sm text-gray-700">
                  Open QuickBooks Desktop and go to <span className="font-semibold">File → Create Backup</span>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                  2
                </div>
                <div className="text-sm text-gray-700">
                  Choose <span className="font-semibold">Local Backup</span> and select a save location
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                  3
                </div>
                <div className="text-sm text-gray-700">
                  Complete the backup process and locate your <span className="font-semibold">.QBW file</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Your Conversion Request Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-green-600 text-lg">📤</span>
              <h3 className="font-semibold text-gray-900">Submit Your Conversion Request</h3>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-green-600">✉️</span>
                <a
                  href="mailto:qbd2xero@wowzer.tech"
                  className="text-green-600 font-semibold hover:underline"
                >
                  qbd2xero@wowzer.tech
                </a>
              </div>
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-2">Email us with:</p>
                <ul className="space-y-1 ml-4">
                  <li className="flex gap-2">
                    <span className="text-green-600">•</span>
                    <span>Your QuickBooks Desktop backup file (.QBW) as an attachment or download link</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-600">•</span>
                    <span>Payment confirmation (${getCurrentPrice()} USD per conversion)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-600">•</span>
                    <span>Screenshot of the email confirmation you receive from us</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* What Happens Next Section */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">What Happens Next?</h3>
            <div className="space-y-2">
              <div className="flex gap-2 items-start">
                <span className="text-green-600 text-sm">→</span>
                <span className="text-sm text-gray-700">We'll convert your QuickBooks Desktop data to Xero format</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-green-600 text-sm">→</span>
                <span className="text-sm text-gray-700">Import the converted data into a new Xero organization</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-green-600 text-sm">→</span>
                <span className="text-sm text-gray-700">Send you an invitation to claim ownership of the Xero organization</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-green-600 text-sm">→</span>
                <span className="text-sm text-gray-700">Complete the entire process within <span className="font-semibold">1 business day</span></span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {paymentError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{paymentError}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              className="w-full bg-cyan-400 hover:bg-cyan-500 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              onClick={handleQBDPayment}
              disabled={paymentLoading}
            >
              {paymentLoading ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Processing...
                </>
              ) : (
                <>
                  <span className="text-lg">💳</span>
                  Pay ${numberOfMigrations * getCurrentPrice()} USD
                </>
              )}
            </button>
            <button
              className="w-full border-2 border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              onClick={() => window.location.href = 'mailto:qbd2xero@wowzer.tech'}
            >
              <span className="text-lg">📧</span>
              Send Email to qbd2xero@wowzer.tech
            </button>
          </div>
        </div>
      </Modal>

      {/* QBD to Xero Payment Result Modal */}
      <QbdXeroPaymentResultModal
        modalOpen={paymentResultModalOpen}
        onModalClose={() => setPaymentResultModalOpen(false)}
        paymentData={paymentResultData}
        onTryAgain={() => {
          setPaymentResultModalOpen(false);
          setQbdToXeroModalOpen(true);
        }}
      />

      {isLargeScreen && (
        <div className="organizations-table-wrap">
          <Table
            sticky={true}
            rowKey="statusKey"
            dataSource={filteredOrganizations}
            rowSelection={rowSelection}
            columns={columns.filter(Boolean)}
            loading={tableDataLoading || isLoading}
            pagination={{
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              defaultPageSize: 10,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            size="small"
            locale={{
              emptyText: getTableEmptyText(),
            }}
          />
        </div>
      )}

      {!isLargeScreen && (
        <div>
          {hasNoOrganizations ? renderOrganizationsEmptyContent() : mobileRender}
        </div>
      )}

      {/* Support component with ref for organization-specific support */}
      <Support ref={supportRef} showTrigger={false} />
        </div>
      </div>

      {/* Loading overlay with spinner */}
      {addOrgLoading && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50">
          <Spin size="large" />
        </div>
      )}
      <AddOrgTypeModal
        open={addOrgTypeModalOpen}
        onClose={() => setAddOrgTypeModalOpen(false)}
        onSelect={handleProviderSelect}
      />
    </div>
  );
};

export default Accounts;
